import { useEffect, useMemo, useState } from "react";
import {
  Baby,
  ArrowLeft,
  BarChart3,
  Bike,
  Bell,
  CalendarCheck,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  ClipboardList,
  ExternalLink,
  Filter,
  Gauge,
  HeartHandshake,
  Home,
  KeyRound,
  Leaf,
  ListChecks,
  MapPin,
  PackageCheck,
  Phone,
  Radar,
  QrCode,
  Recycle,
  Search,
  ShieldCheck,
  Store,
  TrendingUp,
  Truck,
  UploadCloud,
  UserRound,
  Wrench,
  X
} from "lucide-react";
import {
  API_ERROR_EVENT,
  convertAdminIntakeQuoteToProcurement,
  convertAdminOpportunityToProcurement,
  convertAdminPartnerOfferToProcurement,
  createAdminCarbonLedgerEntry,
  createAdminImpactMetric,
  createAdminFulfillmentTask,
  createAdminInspectionTemplate,
  createAdminIntakeQuote,
  createAdminMarketInsight,
  createAdminOpportunity,
  createAdminPartnerOffer,
  createAdminPaymentRecord,
  createAdminPriceEvidence,
  createAdminProcurementContactAttempt,
  createAdminProcurementPaymentRecord,
  createAdminProcurementOrder,
  createAdminProduct,
  createAdminProductInspectionReport,
  createAdminRefurbishmentTask,
  createAdminSupplierPartner,
  createAdminWatchRule,
  createServiceCase,
  importAdminXianyuPriceEvidence,
  importAdminXianyuVisibleSampleText,
  importAdminXianyuVisibleSampleTextAndConvertToProcurement,
  loadAdminDashboard,
  loadBootstrap,
  loadOperatorRadar,
  loadPublicItem,
  loadAdminPricingGuide,
  markAdminNotificationRead,
  notifyAdminProcurementExecutionQueue,
  previewAdminXianyuVisibleSamples,
  requestJson,
  recordAdminIntakeEvent,
  recordAdminFulfillmentEvent,
  recordAdminOpportunityAction,
  recordAdminPartnerOfferEvent,
  recordAdminRefurbishmentEvent,
  recordAdminServiceCaseEvent,
  updateAdminInspectionTemplateStatus,
  updateAdminIntakeStatus,
  updateAdminIntakeQuoteStatus,
  updateAdminOrderStatus,
  updateAdminProductInspectionReportStatus,
  updateAdminProductStatus,
  updateAdminSupplierPartnerStatus
} from "./api";
import type { ApiErrorNotice } from "./api";
import type { XianyuVisibleImportAndProcurementResult, XianyuVisibleImportResult, XianyuVisibleSamplePreview } from "./api";
import { brandHouses, buildSourcingPulse, describeFairOffer, matchBrandHouse } from "./brandHouses";
import type { BrandHouseId, SourcingPulse } from "./brandHouses";
import { emptyPayload } from "./data";
import { AdminPortal, clearAdminSession } from "./AdminPortal";
import { LoadingOverlay } from "./LoadingOverlay";
import { PullToRefresh } from "./PullToRefresh";
import { Topbar } from "./Topbar";
import { TradeLedgerPanel } from "./TradeLedger";
import type { LeanGrowthPipeline } from "./growthPipeline";
import type {
  AdminCarbonLedgerEntry,
  AdminImpactMetric,
  AdminFulfillmentTask,
  AdminIntakeLead,
  AdminIntakeQuote,
  AdminInspectionTemplate,
  AdminMarketInsight,
  AdminNotificationMessage,
  AdminOpportunity,
  AdminOpportunityImportRun,
  AdminOrder,
  AdminPartnerOffer,
  AdminPaymentRecord,
  AdminPriceEvidence,
  AdminPricingGuide,
  AdminProcurementExecutionItem,
  AdminProcurementPaymentRecord,
  AdminProcurementOrder,
  AdminProduct,
  AdminProductInspectionReport,
  AdminOverview,
  AdminReadiness,
  AdminRefurbishmentTask,
  AdminServiceCase,
  AdminSupplierPartner,
  AdminUnitEconomics,
  AdminWatchRule,
  BootstrapPayload,
  CarbonLedgerEntryType,
  Category,
  FulfillmentStatus,
  FulfillmentTaskType,
  ImpactMetric,
  IntakeEventType,
  IntakeQuoteStatus,
  InspectionTemplateSeverity,
  InspectionTemplateStatus,
  InspectionItem,
  MarketInsight,
  Opportunity,
  PartnerOfferStatus,
  PaymentChannel,
  PaymentStatus,
  PaymentType,
  PriceEvidenceSource,
  ProcurementContactChannel,
  ProcurementContactPickupMethod,
  ProcurementContactResult,
  ProcurementPaymentType,
  ProcurementPaymentStatus,
  ProcurementSellerCredit,
  ProcurementSellerResponseQuality,
  ProcurementSellerRiskLevel,
  ProcurementSellerType,
  ProcurementSourceType,
  ProcurementStatus,
  ProductInspectionStatus,
  Product,
  ProductStatus,
  PublicItemDetail,
  RefurbishmentStatus,
  RefurbishmentTaskType,
  SupplierPartnerStatus,
  SupplierPartnerType
} from "./types";

type TabKey = "store" | "sell" | "care" | "radar" | "ops";
type CategoryFilter = "all" | Category;

const currency = new Intl.NumberFormat("zh-CN", {
  style: "currency",
  currency: "CNY",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const storeTabs: Array<{ key: TabKey; label: string; icon: typeof Home }> = [
  { key: "store", label: "店面", icon: Store },
  { key: "sell", label: "回收", icon: Recycle },
  { key: "care", label: "售后", icon: ShieldCheck }
];

const tabs: Array<{ key: TabKey; label: string; icon: typeof Home }> = [
  ...storeTabs,
  { key: "ops", label: "管理", icon: BarChart3 }
];

function resolveTabKey(value: string | null): TabKey | null {
  if (value === "market") {
    return "store";
  }
  if (value === "impact") {
    return "care";
  }
  if (value === "radar") {
    return "ops";
  }
  return tabs.some((tab) => tab.key === value) ? (value as TabKey) : null;
}

const categories: Array<{ key: CategoryFilter; label: string; icon: typeof Recycle }> = [
  { key: "all", label: "全部", icon: Recycle },
  { key: "mobility", label: "低碳出行", icon: Bike },
  { key: "baby", label: "母婴耐用", icon: Baby }
];

type ProcurementContactFormState = {
  channel: ProcurementContactChannel;
  result: ProcurementContactResult;
  sellerResponse: string;
  confirmedPrice: string;
  pickupMethod: ProcurementContactPickupMethod;
  sellerType: ProcurementSellerType;
  sellerCredit: ProcurementSellerCredit;
  sameSkuCount: string;
  usesRealPhotos: boolean;
  supportsVideo: boolean;
  supportsEscrow: boolean;
  supportsInspection: boolean;
  asksOffPlatformPayment: boolean;
  refusesVerification: boolean;
  priceAnomaly: boolean;
  responseQuality: ProcurementSellerResponseQuality;
  nextFollowUpAt: string;
  riskFlags: string;
  operatorNote: string;
};

const procurementContactChannelOptions: Array<{ value: ProcurementContactChannel; label: string }> = [
  { value: "xianyu", label: "闲鱼" },
  { value: "wechat", label: "微信" },
  { value: "phone", label: "电话" },
  { value: "sms", label: "短信" },
  { value: "offline", label: "线下" },
  { value: "other", label: "其他" }
];

const procurementContactResultOptions: Array<{ value: ProcurementContactResult; label: string }> = [
  { value: "available", label: "在售可谈" },
  { value: "price_confirmed", label: "价格确认" },
  { value: "no_reply", label: "暂无回复" },
  { value: "sold", label: "已售出" },
  { value: "risky", label: "风险放弃" }
];

const procurementPickupMethodOptions: Array<{ value: ProcurementContactPickupMethod; label: string }> = [
  { value: "unknown", label: "未定" },
  { value: "ship", label: "邮寄" },
  { value: "pickup", label: "自提" },
  { value: "either", label: "均可" }
];

const procurementSellerTypeOptions: Array<{ value: ProcurementSellerType; label: string }> = [
  { value: "unknown", label: "未确认" },
  { value: "personal_idle", label: "个人闲置" },
  { value: "fish_shop", label: "鱼铺/批量" },
  { value: "professional", label: "职业卖家" }
];

const procurementSellerCreditOptions: Array<{ value: ProcurementSellerCredit; label: string }> = [
  { value: "unknown", label: "未确认" },
  { value: "excellent", label: "优秀" },
  { value: "good", label: "良好" },
  { value: "fair", label: "一般" },
  { value: "poor", label: "较差" }
];

const procurementResponseQualityOptions: Array<{ value: ProcurementSellerResponseQuality; label: string }> = [
  { value: "unknown", label: "未判断" },
  { value: "clear", label: "回复清晰" },
  { value: "vague", label: "含糊" },
  { value: "pushy", label: "催付款" }
];

function defaultProcurementContactForm(item: AdminProcurementExecutionItem): ProcurementContactFormState {
  const shouldConfirmPrice = item.next_action === "confirm_payment_terms";
  const agreedPrice = Number(item.agreed_price ?? 0);

  return {
    channel: "xianyu",
    result: shouldConfirmPrice ? "price_confirmed" : "available",
    sellerResponse: "",
    confirmedPrice: shouldConfirmPrice && Number.isFinite(agreedPrice) && agreedPrice > 0 ? String(agreedPrice) : "",
    pickupMethod: "unknown",
    sellerType: "unknown",
    sellerCredit: "unknown",
    sameSkuCount: "1",
    usesRealPhotos: false,
    supportsVideo: false,
    supportsEscrow: false,
    supportsInspection: false,
    asksOffPlatformPayment: false,
    refusesVerification: false,
    priceAnomaly: false,
    responseQuality: "unknown",
    nextFollowUpAt: "",
    riskFlags: "",
    operatorNote: ""
  };
}

function parseProcurementRiskFlags(value: string) {
  return value
    .split(/[,，、\n]/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 8);
}

function getInitialTab(): TabKey {
  return resolveTabKey(new URLSearchParams(window.location.search).get("tab")) ?? "store";
}

function getInitialHouse(): BrandHouseId | "all" {
  const house = new URLSearchParams(window.location.search).get("house");
  return brandHouses.some((item) => item.id === house) ? (house as BrandHouseId) : "all";
}

function getInitialPublicItemToken() {
  return new URLSearchParams(window.location.search).get("item")?.trim() ?? "";
}

export function App() {
  const [payload, setPayload] = useState<BootstrapPayload>(emptyPayload);
  const [activeTab, setActiveTab] = useState<TabKey>(getInitialTab);
  const [houseFilter, setHouseFilter] = useState<BrandHouseId | "all">(getInitialHouse);
  const [operatorMode, setOperatorMode] = useState(() => {
    const tab = getInitialTab();
    return tab === "ops";
  });
  const [category, setCategory] = useState<CategoryFilter>("all");
  const [query, setQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [operationError, setOperationError] = useState<ApiErrorNotice | null>(null);
  const [publicItemToken, setPublicItemToken] = useState(getInitialPublicItemToken);
  const [publicItem, setPublicItem] = useState<PublicItemDetail | null>(null);
  const [publicItemState, setPublicItemState] = useState<"idle" | "loading" | "ready" | "error">(
    publicItemToken ? "loading" : "idle"
  );
  const [publicItemReloadKey, setPublicItemReloadKey] = useState(0);
  const [operatorOpportunities, setOperatorOpportunities] = useState<Opportunity[]>([]);
  const [operatorRules, setOperatorRules] = useState<BootstrapPayload["watchRules"]>([]);
  const [operatorSourceStatus, setOperatorSourceStatus] = useState<NonNullable<BootstrapPayload["sourceStatus"]>>([]);
  const [operatorPulse, setOperatorPulse] = useState<SourcingPulse[] | null>(null);
  const [radarState, setRadarState] = useState<"idle" | "loading" | "ready" | "auth">("idle");
  const visibleTabs = storeTabs;

  useEffect(() => {
    const onApiError = (event: Event) => {
      const detail = (event as CustomEvent<ApiErrorNotice>).detail;
      if (detail) {
        setOperationError(detail);
      }
    };
    window.addEventListener(API_ERROR_EVENT, onApiError);
    return () => window.removeEventListener(API_ERROR_EVENT, onApiError);
  }, []);

  async function refreshPublicPage() {
    setLoadState("loading");
    try {
      const data = await loadBootstrap();
      setPayload(data);
      setLoadState("ready");
    } catch {
      setLoadState("error");
    }
  }

  useEffect(() => {
    void refreshPublicPage();
  }, []);

  useEffect(() => {
    if (!operatorMode) {
      return;
    }

    const token = localStorage.getItem("yoyo-admin-token");
    if (!token) {
      setRadarState("auth");
      return;
    }

    let cancelled = false;
    setRadarState("loading");
    void loadOperatorRadar(token)
      .then((radar) => {
        if (cancelled) {
          return;
        }
        setOperatorOpportunities(radar.opportunities);
        setOperatorRules(radar.watchRules);
        setOperatorSourceStatus(radar.sourceStatus ?? []);
        setOperatorPulse(radar.sourcingPulse ?? null);
        setRadarState("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setRadarState("auth");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [operatorMode]);

  useEffect(() => {
    if (!publicItemToken) {
      setPublicItem(null);
      setPublicItemState("idle");
      return;
    }

    let cancelled = false;
    setPublicItemState("loading");
    void loadPublicItem(publicItemToken)
      .then((item) => {
        if (!cancelled) {
          setPublicItem(item);
          setPublicItemState("ready");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPublicItem(null);
          setPublicItemState("error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [publicItemReloadKey, publicItemToken]);

  const searchedProducts = useMemo(() => {
    return payload.products.filter((item) => {
      const inCategory = category === "all" || item.category === category;
      const inQuery =
        query.trim().length === 0 ||
        `${item.title} ${item.subcategory} ${item.city} ${item.tags.join(" ")}`
          .toLowerCase()
          .includes(query.toLowerCase());
      return inCategory && inQuery;
    });
  }, [category, payload.products, query]);

  const products = useMemo(() => {
    return searchedProducts.filter((item) => {
      const house = matchBrandHouse(item);
      return houseFilter === "all" || house?.id === houseFilter;
    });
  }, [houseFilter, searchedProducts]);

  const opportunities = useMemo(() => {
    return operatorOpportunities
      .filter((item) => category === "all" || item.category === category)
      .sort((a, b) => b.score - a.score);
  }, [category, operatorOpportunities]);
  const sourcingPulse = useMemo(
    () => operatorPulse ?? payload.sourcingPulse ?? buildSourcingPulse(payload.opportunities),
    [operatorPulse, payload.opportunities, payload.sourcingPulse]
  );
  const visibleOpportunities = useMemo(() => {
    if (houseFilter === "all") {
      return opportunities;
    }
    return opportunities.filter((item) => matchBrandHouse(item)?.id === houseFilter);
  }, [houseFilter, opportunities]);

  function selectTab(nextTab: TabKey) {
    const resolvedTab = nextTab === "radar" ? "ops" : nextTab;
    setActiveTab(resolvedTab);
    if (resolvedTab === "ops") {
      setOperatorMode(true);
    }
    const url = new URL(window.location.href);
    url.searchParams.set("tab", resolvedTab);
    window.history.replaceState(null, "", url);
  }

  function selectHouse(nextHouse: BrandHouseId | "all") {
    setHouseFilter(nextHouse);
    const url = new URL(window.location.href);
    if (nextHouse === "all") {
      url.searchParams.delete("house");
    } else {
      url.searchParams.set("house", nextHouse);
    }
    window.history.replaceState(null, "", url);
  }

  function exitOperatorMode() {
    clearAdminSession();
    setOperatorMode(false);
    selectTab("store");
  }

  function closePublicItem() {
    const url = new URL(window.location.href);
    url.searchParams.delete("item");
    window.history.replaceState(null, "", url);
    setPublicItemToken("");
  }

  function retryPublicItem() {
    setPublicItemReloadKey((value) => value + 1);
  }

  if (publicItemToken) {
    return (
      <div className="app-shell public-item-shell">
        {operationError && <OperationErrorNotice error={operationError} onDismiss={() => setOperationError(null)} />}
        <PullToRefresh onRefresh={async () => retryPublicItem()}>
          <PublicItemScreen
            item={publicItem}
            state={publicItemState}
            onBack={closePublicItem}
            onRetry={retryPublicItem}
          />
        </PullToRefresh>
      </div>
    );
  }

  return (
    <div className={operatorMode ? "app-shell admin-shell" : "app-shell"}>
      <Topbar
        operatorMode={operatorMode}
        onHome={() => selectTab(operatorMode ? "ops" : "store")}
        onExit={exitOperatorMode}
        onLogin={() => selectTab("ops")}
      />

      {operationError && <OperationErrorNotice error={operationError} onDismiss={() => setOperationError(null)} />}

      <PullToRefresh onRefresh={refreshPublicPage} disabled={operatorMode} className="app-content-refresh">
      <main className="app-main">
        <LoadingOverlay active={!operatorMode && loadState === "loading"} label="正在读取真实在售商品" />
        {!operatorMode && loadState === "error" && (
          <StatusBanner tone="danger" label="店面暂不可用，请稍后下拉重试；不会展示模拟库存" />
        )}
        {activeTab === "store" && <SearchBar value={query} onChange={setQuery} />}

        {activeTab === "store" && (
          <StoreView
            products={products}
            catalog={searchedProducts}
            houseFilter={houseFilter}
            onSelectHouse={selectHouse}
            onSelect={setSelectedProduct}
            onNavigate={selectTab}
          />
        )}
        {activeTab === "radar" && (
          <RadarView
            opportunities={visibleOpportunities}
            houseFilter={houseFilter}
            sourcingPulse={sourcingPulse}
            rules={operatorRules}
            sourceStatus={operatorSourceStatus}
            radarState={radarState}
            onSelectHouse={selectHouse}
            onNavigate={selectTab}
          />
        )}
        {activeTab === "sell" && <SellView />}
        {activeTab === "care" && <CareView payload={payload} />}
        {activeTab === "ops" && <AdminPortal />}
        {(activeTab === "store" || activeTab === "sell" || activeTab === "care") && <LegalFooter />}
      </main>
      </PullToRefresh>

      {!operatorMode && <nav className="bottom-nav cols-3" aria-label="主导航">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              className={tab.key === activeTab ? "nav-item active" : "nav-item"}
              type="button"
              key={tab.key}
              onClick={() => selectTab(tab.key)}
              aria-pressed={tab.key === activeTab}
            >
              <Icon size={20} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>}

      {selectedProduct && (
        <ProductDetailSheet product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}
    </div>
  );
}

function LegalFooter() {
  return (
    <footer className="legal-footer">
      <a href="/legal/privacy">隐私</a>
      <a href="/legal/terms">用户协议</a>
      <a href="/legal/warranty">质保售后</a>
    </footer>
  );
}

function StatusBanner({ tone, label }: { tone: "neutral" | "danger"; label: string }) {
  return (
    <section className={`status-banner ${tone}`} aria-live="polite">
      <ShieldCheck size={17} />
      <span>{label}</span>
    </section>
  );
}

function OperationErrorNotice({ error, onDismiss }: { error: ApiErrorNotice; onDismiss: () => void }) {
  return (
    <aside className="operation-error-notice" role="alert" aria-live="assertive">
      <div>
        <strong>{error.message}</strong>
        <small>{error.detail}</small>
      </div>
      <button type="button" aria-label="关闭错误详情" onClick={onDismiss}><X size={16} /></button>
    </aside>
  );
}

function PublicItemScreen({
  item,
  state,
  onBack,
  onRetry
}: {
  item: PublicItemDetail | null;
  state: "idle" | "loading" | "ready" | "error";
  onBack: () => void;
  onRetry: () => void;
}) {
  const loadingOverlay = <LoadingOverlay active={state === "loading"} label="正在读取物品档案" />;

  if (state === "loading") {
    return (
      <>{loadingOverlay}<main className="public-item-view" aria-live="polite" /></>
    );
  }

  if (state === "error" || !item) {
    return (
      <>{loadingOverlay}<main className="public-item-view" aria-live="polite">
        <button className="public-back-button" type="button" onClick={onBack}>
          <ArrowLeft size={18} />
          <span>返回店面</span>
        </button>
        <section className="public-item-state error">
          <ShieldCheck size={24} />
          <strong>未找到物品档案</strong>
          <span>请确认二维码来自 YooY 真实商品标签</span>
          <button className="primary-button" type="button" onClick={onRetry}>
            重新读取
          </button>
        </section>
      </main></>
    );
  }

  const reportItems = item.inspectionReport?.items ?? [];
  const inspectionItems = reportItems.length > 0 ? reportItems : item.inspection;
  const house = matchBrandHouse(item);
  const offer = describeFairOffer(item);

  return (
    <>{loadingOverlay}<main className="public-item-view">
      <button className="public-back-button" type="button" onClick={onBack}>
        <ArrowLeft size={18} />
        <span>返回店面</span>
      </button>

      <section className="public-item-hero">
        <img src={item.image} alt={item.title} />
        <div className="public-hero-copy">
          <span className="pill strong">
            <QrCode size={15} />
            {item.itemCode}
          </span>
          <h1>{item.title}</h1>
          <p>{item.city} · {item.subcategory} · {translateProductStatus(item.lifecycleStatus)}</p>
        </div>
      </section>

      <section className="public-metric-grid" aria-label="物品核心指标">
        <div>
          <ShieldCheck size={18} />
          <strong>{item.safetyScore}</strong>
          <span>安全分</span>
        </div>
        <div>
          <PackageCheck size={18} />
          <strong>{item.warrantyDays}</strong>
          <span>质保天数</span>
        </div>
        <div>
          <Leaf size={18} />
          <strong>{item.carbonKg}kg</strong>
          <span>低碳减排</span>
        </div>
      </section>

      <section className="public-item-section">
        <div className="mini-section-head">
          <CircleDollarSign size={18} />
          <h2>{offer.headline}</h2>
        </div>
        <p className="public-offer-note">{offer.note}</p>
        {house && (
          <div className="service-strip">
            <strong>{house.warranty}</strong>
            <span>不收：{house.reject}</span>
          </div>
        )}
      </section>

      <section className="public-item-section">
        <div className="mini-section-head">
          <ClipboardList size={18} />
          <h2>质检结果</h2>
        </div>
        <div className="public-check-summary" aria-label="质检统计">
          <span>通过 {item.inspectionSummary.pass}</span>
          <span>关注 {item.inspectionSummary.watch}</span>
          <span>未过 {item.inspectionSummary.fail}</span>
        </div>
        {item.inspectionReport && (
          <div className="public-report-strip">
            <strong>{translateProductInspectionStatus(item.inspectionReport.status)}</strong>
            <span>{item.inspectionReport.summary || "正式质检已记录"}</span>
            <small>{formatPublicDate(item.inspectionReport.updatedAt)}</small>
          </div>
        )}
        <div className="inspection-list">
          {inspectionItems.map((check) => (
            <div className="inspection-row" key={`${check.label}-${check.detail}`}>
              <span className={`check-dot ${check.result}`} />
              <div>
                <strong>{check.label}</strong>
                <small>{check.detail}</small>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="public-item-section">
        <div className="mini-section-head">
          <PackageCheck size={18} />
          <h2>物品档案</h2>
        </div>
        <div className="public-fact-grid">
          <span>
            <small>成色</small>
            <strong>{item.condition}</strong>
          </span>
          <span>
            <small>当前售价</small>
            <strong>{currency.format(item.price)}</strong>
          </span>
          <span>
            <small>原价参考</small>
            <strong>{currency.format(item.originalPrice)}</strong>
          </span>
          <span>
            <small>更新时间</small>
            <strong>{formatPublicDate(item.updatedAt)}</strong>
          </span>
        </div>
        <div className="tag-row public-tags">
          {item.tags.map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
      </section>
    </main></>
  );
}

function SearchBar({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <section className="search-band search-band-solo" aria-label="商品搜索">
      <label className="search-box">
        <Search size={18} aria-hidden="true" />
        <span className="sr-only">搜索</span>
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="搜酷骑、口袋车、腰凳"
          type="search"
        />
      </label>
    </section>
  );
}

function CategoryTabs({
  value,
  onChange
}: {
  value: CategoryFilter;
  onChange: (value: CategoryFilter) => void;
}) {
  return (
    <div className="segmented" role="tablist" aria-label="品类">
      {categories.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.key}
            className={value === item.key ? "segment active" : "segment"}
            type="button"
            role="tab"
            aria-selected={value === item.key}
            onClick={() => onChange(item.key)}
          >
            <Icon size={16} />
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function StoreView({
  products,
  catalog,
  houseFilter,
  onSelectHouse,
  onSelect,
  onNavigate
}: {
  products: Product[];
  catalog: Product[];
  houseFilter: BrandHouseId | "all";
  onSelectHouse: (house: BrandHouseId | "all") => void;
  onSelect: (product: Product) => void;
  onNavigate: (tab: TabKey) => void;
}) {
  const featured = products[0];
  const rest = products.slice(1);
  const activeHouse = brandHouses.find((house) => house.id === houseFilter);
  const houseCounts = {
    all: catalog.length,
    cooghi: catalog.filter((item) => matchBrandHouse(item)?.id === "cooghi").length,
    gb: catalog.filter((item) => matchBrandHouse(item)?.id === "gb").length,
    babycare: catalog.filter((item) => matchBrandHouse(item)?.id === "babycare").length
  };

  return (
    <div className="view-stack store-view">
      <section className="house-rail" aria-label="品牌馆">
        <button
          className={houseFilter === "all" ? "house-card active" : "house-card"}
          type="button"
          onClick={() => onSelectHouse("all")}
        >
          <span>全部馆</span>
          <strong>在售精选</strong>
          <small>{houseCounts.all} 件可看</small>
        </button>
        {brandHouses.map((house) => (
          <button
            className={houseFilter === house.id ? "house-card active" : "house-card"}
            type="button"
            key={house.id}
            onClick={() => onSelectHouse(house.id)}
          >
            <span>{house.brand}</span>
            <strong>{house.name}</strong>
            <small>{houseCounts[house.id]} 件 · {house.focus}</small>
          </button>
        ))}
      </section>

      {activeHouse && (
        <section className="house-note">
          <strong>{activeHouse.promise}</strong>
          <span>不收：{activeHouse.reject}</span>
        </section>
      )}

      {featured ? (
        <FeaturedDeal product={featured} onSelect={() => onSelect(featured)} />
      ) : (
        <SourcingEmptyState
          house={activeHouse}
          onSell={() => onNavigate("sell")}
        />
      )}

      {rest.length > 0 && (
        <div className="product-list">
          {rest.map((product) => (
            <ProductCard key={product.id} product={product} onSelect={() => onSelect(product)} />
          ))}
        </div>
      )}

    </div>
  );
}

function ProfitTargetPanel({ pipeline }: { pipeline: LeanGrowthPipeline }) {
  const target = pipeline.profitTarget;
  const progress = Math.min(100, Math.round((target.expectedNetProfit / target.targetNetProfit) * 100));

  return (
    <section className="profit-target-panel" aria-label="月盈利目标">
      <div className="profit-target-head">
        <div>
          <p className="eyebrow">月盈利目标</p>
          <h2>{currency.format(target.targetNetProfit)}+ 净毛利</h2>
        </div>
        <strong>{progress}%</strong>
      </div>
      <div className="profit-bar" aria-hidden="true">
        <span style={{ width: `${progress}%` }} />
      </div>
      <div className="profit-metrics">
        <span>
          <b>{currency.format(target.expectedNetProfit)}</b>
          <small>模型预估</small>
        </span>
        <span>
          <b>{target.monthlyUnits} 单</b>
          <small>月周转</small>
        </span>
        <span>
          <b>{target.skuTargets.length} 个</b>
          <small>核心 SKU</small>
        </span>
      </div>
      <div className="sku-target-grid">
        {target.skuTargets.map((sku) => (
          <article key={sku.product}>
            <span>{sku.tier} · {sku.categoryPath.split(" > ").slice(-1)[0]}</span>
            <strong>{sku.product}</strong>
            <small>收 {sku.buyPriceRange} / 出 {sku.resalePriceRange} · {sku.monthlyUnits} 单/月 · 净利 {sku.netProfitPerUnit}</small>
          </article>
        ))}
      </div>
    </section>
  );
}

function GrowthHero({
  opportunities,
  onNavigate
}: {
  opportunities: Opportunity[];
  onNavigate: (tab: TabKey) => void;
}) {
  const topOpportunity = opportunities[0];
  const estimatedMargin = topOpportunity ? topOpportunity.fairPrice - topOpportunity.askPrice : 0;

  return (
    <section className="growth-hero">
      <div className="growth-hero-copy">
        <p className="eyebrow">真实机会 · 快速采买入口</p>
        <h2>先看商品和价格，再联系卖家；把高分低价机会推进到采购执行。</h2>
        <p>
          首屏只保留采买最关键的信息：商品说明、采买价、参考卖价、预计毛利、卖家信号和风险动作。
        </p>
      </div>
      <div className="growth-hero-actions" aria-label="增长动作">
        <button className="primary-button" type="button" onClick={() => onNavigate("radar")}>
          <Radar size={17} />
          <span>看机会榜</span>
        </button>
        <button className="text-button hero-secondary" type="button" onClick={() => onNavigate("ops")}>
          <CalendarCheck size={17} />
          <span>进采购队列</span>
        </button>
      </div>
      {topOpportunity && (
        <div className="hero-signal">
          <span>当前热推</span>
          <strong>{topOpportunity.title}</strong>
          <small>
            {topOpportunity.city} · 采买 {currency.format(topOpportunity.askPrice)} · 参考 {currency.format(topOpportunity.fairPrice)}
          </small>
          <small>
            分数 {topOpportunity.score} · 预计毛利 {currency.format(estimatedMargin)} · {translateOpportunityRisk(topOpportunity.risk)}
          </small>
        </div>
      )}
    </section>
  );
}

function ProcurementQuickEntry({
  opportunities,
  onNavigate
}: {
  opportunities: Opportunity[];
  onNavigate: (tab: TabKey) => void;
}) {
  const hotCount = opportunities.filter((item) => item.action === "bid" || item.score >= 80).length;
  const inspectCount = opportunities.filter((item) => item.action === "inspect").length;
  const topOpportunity = opportunities[0];
  const marginAmount = topOpportunity ? topOpportunity.fairPrice - topOpportunity.askPrice : 0;

  return (
    <section className="procurement-entry-panel" aria-label="机会采买快入口">
      <div className="section-head compact">
        <div>
          <p className="eyebrow">采买快入口</p>
          <h2>今天先处理能买、值得买、风险可控的货</h2>
        </div>
      </div>
      <div className="procurement-entry-grid">
        <button className="procurement-entry-card primary" type="button" onClick={() => onNavigate("radar")}>
          <Radar size={18} />
          <span>机会榜</span>
          <strong>{hotCount}</strong>
          <small>高分/建议采买 · 共 {opportunities.length} 条真实机会</small>
        </button>
        <button className="procurement-entry-card" type="button" onClick={() => onNavigate("ops")}>
          <CalendarCheck size={18} />
          <span>采购执行</span>
          <strong>联系</strong>
          <small>打开源商品、保存卖家回复、推进付款到货</small>
        </button>
        <button className="procurement-entry-card" type="button" onClick={() => onNavigate("radar")}>
          <CircleDollarSign size={18} />
          <span>价格判断</span>
          <strong>{topOpportunity ? currency.format(marginAmount) : "待机会"}</strong>
          <small>{topOpportunity ? `${topOpportunity.title} · 待复核` : `${inspectCount} 条需要验货后出价`}</small>
        </button>
      </div>
    </section>
  );
}

function PipelineStageRail({ stages }: { stages: LeanGrowthPipeline["stages"] }) {
  return (
    <section className="pipeline-rail" aria-label="一期精益经营闭环">
      {stages.map((stage, index) => (
        <article className={`pipeline-stage ${stage.tone}`} key={stage.key}>
          <span className="stage-index">{index + 1}</span>
          <div>
            <strong>{stage.title}</strong>
            <b>{stage.metric}</b>
            <small>{stage.detail}</small>
          </div>
        </article>
      ))}
    </section>
  );
}

function SignalOpportunityPanel({
  pipeline,
  opportunities,
  onNavigate
}: {
  pipeline: LeanGrowthPipeline;
  opportunities: Opportunity[];
  onNavigate: (tab: TabKey) => void;
}) {
  return (
    <section className="signal-panel">
      <div className="section-head compact">
        <div>
          <p className="eyebrow">平台信号</p>
          <h2>先发现需求，再判断是否值得买</h2>
        </div>
        <button className="text-button" type="button" onClick={() => onNavigate("radar")}>
          <TrendingUp size={17} />
          <span>机会榜</span>
        </button>
      </div>

      <div className="source-readiness-grid">
        {pipeline.sourceReadiness.map((source) => (
          <article className={`source-readiness ${source.status}`} key={source.platform}>
            <span>{source.platform}</span>
            <strong>{source.value}</strong>
            <small>{source.detail}</small>
          </article>
        ))}
      </div>

      {opportunities.length > 0 ? (
        <div className="hot-signal-list">
          {opportunities.slice(0, 2).map((opportunity) => (
            <OpportunityCard key={opportunity.id} opportunity={opportunity} onAction={() => onNavigate("ops")} />
          ))}
        </div>
      ) : (
        <div className="signal-empty">
          <Radar size={22} />
          <strong>等待新机会进入热推池</strong>
          <span>闲鱼监控和小红书需求可以先沉淀规则；有高分价差信号后再进入人工确认。</span>
        </div>
      )}
    </section>
  );
}

function SourcingEmptyState({
  house,
  onSell
}: {
  house?: { name: string };
  onSell: () => void;
}) {
  return (
    <section className="sourcing-empty" aria-label="商品状态">
      <div className="sourcing-empty-icon" aria-hidden="true">
        <ShieldCheck size={20} />
      </div>
      <div className="sourcing-empty-copy">
        <strong>{house ? `${house.name}暂无在售` : "暂无在售商品"}</strong>
        <span>通过质检后才会上架，看到的每一件都值得放心。</span>
      </div>
      <button className="text-button sourcing-empty-action" type="button" onClick={onSell}>
        送来闲置估个价
        <ChevronRight size={16} />
      </button>
    </section>
  );
}

function FeaturedDeal({ product, onSelect }: { product: Product; onSelect: () => void }) {
  const house = matchBrandHouse(product);
  const offer = describeFairOffer(product);

  return (
    <section className="featured-deal">
      <img src={product.image} alt={product.title} />
      <div className="featured-copy">
        <span className="pill strong">
          <ShieldCheck size={15} />
          {house ? house.name : "YooY 质检"}
        </span>
        <h2>{product.title}</h2>
        <div className="price-row">
          <strong>{currency.format(product.price)}</strong>
          <span>参考 {currency.format(product.originalPrice)}</span>
        </div>
        <p className="fair-offer-line">{offer.headline} · {offer.note}</p>
        <div className="metric-row">
          <span>
            <PackageCheck size={15} />
            {product.warrantyDays} 天质保
          </span>
          <span>
            <Gauge size={15} />
            安全 {product.safetyScore}
          </span>
        </div>
        <button className="featured-action" type="button" onClick={onSelect}>
          <CalendarCheck size={17} />
          <span>看档案并预约</span>
        </button>
      </div>
    </section>
  );
}

function ProductCard({ product, onSelect }: { product: Product; onSelect: () => void }) {
  const house = matchBrandHouse(product);

  return (
    <article className="product-card">
      <button className="product-card-hit" type="button" onClick={onSelect}>
        <img src={product.image} alt="" />
        <span className="sr-only">查看 {product.title}</span>
      </button>
      <div className="product-copy">
        <div className="product-title-row">
          <h3>{product.title}</h3>
          <span className="condition">{house?.name ?? product.condition}</span>
        </div>
        <p>{product.city} · {product.subcategory} · {product.warrantyDays} 天质保</p>
        <div className="tag-row">
          {product.tags.slice(0, 3).map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
        <div className="card-bottom">
          <div className="price-row compact">
            <strong>{currency.format(product.price)}</strong>
            <span>参考 {currency.format(product.originalPrice)}</span>
          </div>
          <button className="round-link" type="button" aria-label={`查看 ${product.title}`} onClick={onSelect}>
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </article>
  );
}

function ProductDetailSheet({ product, onClose }: { product: Product; onClose: () => void }) {
  const house = matchBrandHouse(product);
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [submittedOrderId, setSubmittedOrderId] = useState<number | null>(null);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    setStatus("loading");

    try {
      const result = await requestJson<{ id: number }>("/api/orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productId: product.id,
          customerName: formData.get("customerName"),
          contact: formData.get("contact"),
          city: formData.get("city"),
          intent: formData.get("intent")
        })
      });
      setSubmittedOrderId(result.id);
      setStatus("done");
      form.reset();
    } catch {
      setStatus("error");
    }
  }

  return (
    <div className="sheet-backdrop" role="presentation">
      <section className="product-sheet" role="dialog" aria-modal="true" aria-labelledby="product-sheet-title">
        <div className="sheet-handle" aria-hidden="true" />
        <button className="sheet-close" type="button" aria-label="关闭商品详情" onClick={onClose}>
          <X size={20} />
        </button>

        <img className="sheet-image" src={product.image} alt={product.title} />
        <div className="sheet-copy">
          <p className="eyebrow">
            {house?.name ?? "精选好货"} · {product.city} · {product.subcategory}
          </p>
          <h2 id="product-sheet-title">{product.title}</h2>
          {product.itemCode && (
            <p className="item-code-line">
              <QrCode size={15} />
              {product.itemCode}
            </p>
          )}
          <div className="sheet-price-row">
            <strong>{currency.format(product.price)}</strong>
            <span>参考 {currency.format(product.originalPrice)}</span>
          </div>
          <p className="sheet-house-note">{describeFairOffer(product).note}</p>
          <div className="sheet-metrics">
            <span>
              <ShieldCheck size={15} />
              安全 {product.safetyScore}
            </span>
            <span>
              <Leaf size={15} />
              {product.carbonKg}kg CO2e
            </span>
            <span>
              <PackageCheck size={15} />
              {product.warrantyDays} 天质保
            </span>
          </div>

          {house && (
            <section className="service-strip">
              <strong>{house.warranty}</strong>
              <span>不收：{house.reject}</span>
            </section>
          )}

          {product.publicToken && (
            <a className="care-link" href={`?item=${encodeURIComponent(product.publicToken)}`}>
              <QrCode size={16} />
              <span>打开一物一档</span>
              <ChevronRight size={16} />
            </a>
          )}

          <section className="inspection-panel" aria-label="质检报告">
            <div className="mini-section-head">
              <ClipboardList size={18} />
              <h3>一物一档质检</h3>
            </div>
            <div className="inspection-list">
              {product.inspection.map((item) => (
                <div className="inspection-row" key={item.label}>
                  <span className={`check-dot ${item.result}`} />
                  <div>
                    <strong>{item.label}</strong>
                    <small>{item.detail}</small>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <form className="order-form" onSubmit={handleSubmit}>
            <div className="mini-section-head">
              <CalendarCheck size={18} />
              <h3>预约看货 / 锁定</h3>
            </div>
            <label>
              <UserRound size={16} />
              <span>姓名</span>
              <input name="customerName" placeholder="姓名" required />
            </label>
            <label>
              <Phone size={16} />
              <span>手机号</span>
              <input name="contact" type="tel" inputMode="tel" autoComplete="tel" placeholder="11 位手机号" pattern="1[3-9][0-9]{9}" required />
            </label>
            <label>
              <MapPin size={16} />
              <span>城市</span>
              <input name="city" placeholder={product.city} required />
            </label>
            <label>
              <ClipboardCheck size={16} />
              <span>意向</span>
              <select name="intent" required defaultValue="reserve">
                <option value="reserve">预约看货</option>
                <option value="buy">锁定购买</option>
                <option value="rent">先租后买</option>
              </select>
            </label>
            <button className="primary-button" type="submit" disabled={status === "loading"}>
              {status === "loading" ? "提交中" : "提交意向"}
            </button>
            {status === "done" && (
              <p className="form-message success">
                <CheckCircle2 size={16} />
                已生成订单 #{submittedOrderId}，请保存订单号
              </p>
            )}
            {status === "error" && <p className="form-message error">提交失败，请稍后再试</p>}
          </form>
        </div>
      </section>
    </div>
  );
}

function RadarView({
  opportunities,
  houseFilter,
  sourcingPulse,
  rules,
  sourceStatus,
  radarState,
  onSelectHouse,
  onNavigate
}: {
  opportunities: Opportunity[];
  houseFilter: BrandHouseId | "all";
  sourcingPulse: SourcingPulse[];
  rules: BootstrapPayload["watchRules"];
  sourceStatus: NonNullable<BootstrapPayload["sourceStatus"]>;
  radarState: "idle" | "loading" | "ready" | "auth";
  onSelectHouse: (house: BrandHouseId | "all") => void;
  onNavigate: (tab: TabKey) => void;
}) {
  const activeHouse = brandHouses.find((house) => house.id === houseFilter);
  const [focus, setFocus] = useState<"action" | "all">("action");
  const actionableCount = opportunities.filter((item) => item.action === "bid" || item.action === "inspect").length;
  const focusedOpportunities = focus === "action"
    ? opportunities.filter((item) => item.action === "bid" || item.action === "inspect")
    : opportunities;

  return (
    <div className="view-stack">
      <section className="radar-summary">
        <div>
          <p className="eyebrow">机会采买</p>
          <h2>{activeHouse ? `${activeHouse.name}机会` : "机会"}</h2>
          <span className="radar-summary-note">
            {actionableCount > 0 ? `${actionableCount} 条需要动作 · 先看价格和风险` : "暂无待处理机会"}
          </span>
        </div>
        <div className="score-badge">
          <TrendingUp size={20} />
          <strong>{opportunities[0]?.score ?? 0}</strong>
        </div>
      </section>

      <div className="radar-focus-bar">
        <div className="radar-count">
          <strong>{focusedOpportunities.length}</strong>
          <span>{focus === "action" ? "条待处理" : "条全部机会"}</span>
        </div>
        <div className="radar-focus-tabs" role="tablist" aria-label="机会筛选">
          <button
            className={focus === "action" ? "active" : ""}
            type="button"
            role="tab"
            aria-selected={focus === "action"}
            onClick={() => setFocus("action")}
          >
            待处理 {actionableCount > 0 && <b>{actionableCount}</b>}
          </button>
          <button
            className={focus === "all" ? "active" : ""}
            type="button"
            role="tab"
            aria-selected={focus === "all"}
            onClick={() => setFocus("all")}
          >
            全部
          </button>
        </div>
      </div>

      <section className="house-rail compact-rail" aria-label="按馆盯盘">
        <button
          className={houseFilter === "all" ? "house-card active" : "house-card"}
          type="button"
          onClick={() => onSelectHouse("all")}
        >
          <span>全部馆</span>
          <strong>盯盘</strong>
          <small>{sourcingPulse.reduce((sum, item) => sum + item.total, 0)} 条</small>
        </button>
        {brandHouses.map((house) => {
          const pulse = sourcingPulse.find((item) => item.id === house.id);
          return (
            <button
              className={houseFilter === house.id ? "house-card active" : "house-card"}
              type="button"
              key={house.id}
              onClick={() => onSelectHouse(house.id)}
            >
              <span>{house.brand}</span>
              <strong>{house.name}</strong>
              <small>{pulse?.bid ?? 0} 待采 · {pulse?.inspect ?? 0} 待验</small>
            </button>
          );
        })}
      </section>

      {radarState === "auth" && (
        <section className="house-note">
          <strong>采买台需要运营连接</strong>
          <span>公开店面不再展示进货价和机会源。连接运营台后，按馆查看待采和待验。</span>
          <button className="text-button" type="button" onClick={() => onNavigate("ops")}>
            去连接运营台
          </button>
        </section>
      )}

        <LoadingOverlay active={radarState === "loading"} label="正在读取受保护的采买机会" />

      <div className="source-status-list">
        {sourceStatus.map((source) => (
          <article className={`source-status ${source.status}`} key={source.label}>
            <Radar size={18} />
            <div>
              <strong>{source.label}</strong>
              <span>{source.value} · {source.detail}</span>
            </div>
          </article>
        ))}
      </div>

      {radarState === "ready" && <ProcurementQuickEntry opportunities={opportunities} onNavigate={onNavigate} />}

      <div className="opportunity-list">
        {focusedOpportunities.length > 0 ? (
          focusedOpportunities.map((item) => (
            <OpportunityCard key={item.id} opportunity={item} onAction={() => onNavigate("ops")} />
          ))
        ) : (
          <EmptyState label={focus === "action" ? "暂无待处理机会" : activeHouse ? `${activeHouse.name}暂无合格机会` : "暂无真实采买机会"} />
        )}
      </div>

      <section className="section-head">
        <div>
          <p className="eyebrow">监控规则</p>
          <h2>实时价差池</h2>
        </div>
      </section>

      <div className="rule-list">
        {rules.map((rule) => (
          <article className="rule-row" key={rule.id}>
            <ClipboardCheck size={18} />
            <div>
              <strong>{rule.keyword}</strong>
              <span>{rule.city} · 最高 {currency.format(rule.maxPrice)} · 分数 {rule.minScore}+</span>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function OpportunityCard({ opportunity, onAction }: { opportunity: Opportunity; onAction?: () => void }) {
  const actionLabel = {
    bid: "采买",
    inspect: "验货",
    watch: "盯盘"
  }[opportunity.action];
  const estimatedMargin = opportunity.fairPrice - opportunity.askPrice;
  const marginRate = Math.round(opportunity.marginRate * 100);
  const sourceUrl = resolveOpportunitySourceUrl(opportunity);
  const house = matchBrandHouse(opportunity);

  return (
    <article className="opportunity-card">
      <div className="opportunity-main">
        <div className="source-row">
          {house && <span className="source-chip house-chip">{house.name}</span>}
          <span className="source-chip">{translateOpportunitySource(opportunity.source)}</span>
          <span className={`risk ${opportunity.risk}`}>{translateOpportunityRisk(opportunity.risk)}</span>
          <span>{opportunity.freshnessMinutes} 分钟</span>
        </div>
        <h3>{opportunity.title}</h3>
        <p className="opportunity-description">
          {translateCategory(opportunity.category)} · {opportunity.city} · {formatSellerSignal(opportunity.sellerSignal)}
        </p>
        <div className="spread-grid">
          <span>
            <small>采买价</small>
            {currency.format(opportunity.askPrice)}
          </span>
          <span>
            <small>参考卖价</small>
            {currency.format(opportunity.fairPrice)}
          </span>
          <span>
            <small>预计毛利</small>
            {currency.format(estimatedMargin)}
          </span>
          <span>
            <small>毛利率</small>
            {marginRate}%
          </span>
        </div>
        <div className="opportunity-insight-row">
          <span>
            <ShieldCheck size={14} />
            {house ? house.reject : translateOpportunityRisk(opportunity.risk)}
          </span>
          <span>
            <ClipboardCheck size={14} />
            {actionLabel}前复核价格、成色和卖家
          </span>
        </div>
      </div>
      <div className="opportunity-action-column">
        {sourceUrl ? (
          <a
            className="source-link-button"
            href={sourceUrl}
            target="_blank"
            rel="noreferrer"
            aria-label={`打开 ${opportunity.title} 的平台商品详情`}
          >
            <ExternalLink size={14} />
            <span>原商品</span>
          </a>
        ) : (
          <span className="source-link-missing">缺源链接</span>
        )}
        <button className="score-button" type="button" aria-label={`${actionLabel} ${opportunity.title}`} onClick={onAction}>
          <strong className="score-value">{opportunity.score}<small>分</small></strong>
          <span>{actionLabel}</span>
        </button>
      </div>
    </article>
  );
}

function SellView() {
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    setStatus("loading");
    const formData = new FormData(form);
    const payload = Object.fromEntries(formData.entries());

    try {
      await requestJson<{ id: number }>("/api/intake", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      setStatus("done");
      form.reset();
    } catch {
      setStatus("error");
    }
  }

  return (
    <div className="view-stack">
      <form className="intake-form" onSubmit={handleSubmit}>
        <div className="form-topline">
          <div>
            <strong>回收估价</strong>
            <span>验收通过，再给报价</span>
          </div>
          <Recycle size={18} aria-hidden="true" />
        </div>
        <label>
          品类
          <select name="category" required defaultValue="baby">
            <option value="baby">母婴耐用品</option>
            <option value="mobility">低碳出行</option>
          </select>
        </label>
        <label>
          物品名称
          <input name="title" placeholder="如 酷骑 S3 / gb 口袋车 / babycare 腰凳" required />
        </label>
        <label>
          所在城市
          <input name="city" placeholder="上海" required />
        </label>
        <label>
          期望价格
          <input name="expectedPrice" inputMode="numeric" placeholder="1500" required />
        </label>
        <label>
          联系方式
          <input name="contact" type="tel" inputMode="tel" autoComplete="tel" placeholder="11 位手机号" pattern="1[3-9][0-9]{9}" required />
        </label>
        <button className="primary-button" type="submit" disabled={status === "loading"}>
          {status === "loading" ? "提交中" : "获取报价"}
        </button>
        {status === "done" && (
          <p className="form-message success">
            <CheckCircle2 size={16} />
            已提交，稍后会联系你
          </p>
        )}
        {status === "error" && <p className="form-message error">提交失败，请稍后再试</p>}
      </form>
    </div>
  );
}

function CareView({ payload }: { payload: BootstrapPayload }) {
  return (
    <div className="view-stack care-view">
      <div className="care-intro">
        <div>
          <strong>售后服务</strong>
          <span>先准备订单号或商品编号，处理会更快</span>
        </div>
        <ShieldCheck size={18} aria-hidden="true" />
      </div>
      <section className="warranty-grid" aria-label="品牌馆质保">
        {brandHouses.map((house) => (
          <article className="warranty-card" key={house.id}>
            <span>{house.name}</span>
            <strong>{house.warranty}</strong>
            <small>不收：{house.reject}</small>
          </article>
        ))}
      </section>

      <section className="reject-panel">
        <h3>明确不卖</h3>
        <p>鱼店铺货、外链付款、无实拍、安全件缺失、吸奶器旧耗材复用。</p>
      </section>

      {payload.impact.length > 0 && (
        <section className="impact-board compact-impact">
          <p className="eyebrow">可核验记录</p>
          <div className="impact-grid">
            {payload.impact.map((metric) => (
              <article key={metric.label}>
                <span>{metric.label}</span>
                <strong>{metric.value}</strong>
                <small>{metric.detail}</small>
              </article>
            ))}
          </div>
        </section>
      )}

      <ServiceCaseForm />
    </div>
  );
}

function ServiceCaseForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const orderId = Number(formData.get("orderId"));
    setStatus("loading");

    try {
      await createServiceCase({
        orderId: Number.isFinite(orderId) && orderId > 0 ? orderId : null,
        productId: String(formData.get("productId") ?? "").trim() || null,
        customerName: String(formData.get("customerName") ?? ""),
        contact: String(formData.get("contact") ?? ""),
        issueType: formData.get("issueType") as AdminServiceCase["issue_type"],
        description: String(formData.get("description") ?? "")
      });
      setStatus("done");
      form.reset();
    } catch {
      setStatus("error");
    }
  }

  return (
    <section className="ops-section">
      <div className="mini-section-head">
        <HeartHandshake size={18} />
        <h3>提交售后</h3>
      </div>
      <form className="ops-product-form" onSubmit={handleSubmit}>
        <div className="ops-form-grid">
          <label>
            <span>订单号</span>
            <input name="orderId" inputMode="numeric" placeholder="可留空" />
          </label>
          <label>
            <span>商品编号</span>
            <input name="productId" placeholder="商品编号或物码，可留空" />
          </label>
        </div>
        <div className="ops-form-grid">
          <label>
            <span>姓名</span>
            <input name="customerName" placeholder="姓名" required />
          </label>
          <label>
            <span>联系</span>
            <input name="contact" type="tel" placeholder="手机号或微信" required />
          </label>
        </div>
        <label>
          <span>问题类型</span>
          <select name="issueType" defaultValue="warranty" required>
            <option value="warranty">质保</option>
            <option value="repair">维修</option>
            <option value="return">退换</option>
            <option value="safety">安全</option>
            <option value="other">其他</option>
          </select>
        </label>
        <label>
          <span>问题说明</span>
          <textarea name="description" placeholder="描述商品问题、发生时间和希望处理方式" required />
        </label>
        <button className="primary-button" type="submit" disabled={status === "loading"}>
          {status === "loading" ? "提交中" : "提交售后工单"}
        </button>
        {status === "done" && (
          <p className="form-message success">
            <CheckCircle2 size={16} />
            售后工单已提交
          </p>
        )}
        {status === "error" && <p className="form-message error">提交失败，请检查订单或商品信息</p>}
      </form>
    </section>
  );
}

function OpsView() {
  const [token, setToken] = useState(() => localStorage.getItem("yoyo-admin-token") ?? "");
  const [draftToken, setDraftToken] = useState(token);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    token ? "loading" : "idle"
  );
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [unitEconomics, setUnitEconomics] = useState<AdminUnitEconomics | null>(null);
  const [readiness, setReadiness] = useState<AdminReadiness | null>(null);
  const [adminProducts, setAdminProducts] = useState<AdminProduct[]>([]);
  const [inspectionTemplates, setInspectionTemplates] = useState<AdminInspectionTemplate[]>([]);
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [paymentRecords, setPaymentRecords] = useState<AdminPaymentRecord[]>([]);
  const [fulfillmentTasks, setFulfillmentTasks] = useState<AdminFulfillmentTask[]>([]);
  const [serviceCases, setServiceCases] = useState<AdminServiceCase[]>([]);
  const [intakeLeads, setIntakeLeads] = useState<AdminIntakeLead[]>([]);
  const [intakeQuotes, setIntakeQuotes] = useState<AdminIntakeQuote[]>([]);
  const [notifications, setNotifications] = useState<AdminNotificationMessage[]>([]);
  const [opportunities, setOpportunities] = useState<AdminOpportunity[]>([]);
  const [opportunityImportRuns, setOpportunityImportRuns] = useState<AdminOpportunityImportRun[]>([]);
  const [supplierPartners, setSupplierPartners] = useState<AdminSupplierPartner[]>([]);
  const [partnerOffers, setPartnerOffers] = useState<AdminPartnerOffer[]>([]);
  const [procurementExecutionItems, setProcurementExecutionItems] = useState<AdminProcurementExecutionItem[]>([]);
  const [procurementContactForms, setProcurementContactForms] = useState<Record<number, ProcurementContactFormState>>(
    {}
  );
  const [procurementOrders, setProcurementOrders] = useState<AdminProcurementOrder[]>([]);
  const [procurementPaymentRecords, setProcurementPaymentRecords] = useState<AdminProcurementPaymentRecord[]>([]);
  const [refurbishmentTasks, setRefurbishmentTasks] = useState<AdminRefurbishmentTask[]>([]);
  const [productInspectionReports, setProductInspectionReports] = useState<AdminProductInspectionReport[]>([]);
  const [watchRules, setWatchRules] = useState<AdminWatchRule[]>([]);
  const [marketInsights, setMarketInsights] = useState<AdminMarketInsight[]>([]);
  const [impactMetrics, setImpactMetrics] = useState<AdminImpactMetric[]>([]);
  const [carbonLedgerEntries, setCarbonLedgerEntries] = useState<AdminCarbonLedgerEntry[]>([]);
  const [priceEvidence, setPriceEvidence] = useState<AdminPriceEvidence[]>([]);
  const [pricingGuide, setPricingGuide] = useState<AdminPricingGuide | null>(null);
  const [productStatus, setProductStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [inspectionTemplateStatus, setInspectionTemplateStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [productLifecycleStatus, setProductLifecycleStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [paymentStatus, setPaymentStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [fulfillmentStatus, setFulfillmentStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [serviceCaseStatus, setServiceCaseStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [opportunityStatus, setOpportunityStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [xianyuVisibleStatus, setXianyuVisibleStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [manualOpportunityStatus, setManualOpportunityStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [supplierPartnerStatus, setSupplierPartnerStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [partnerOfferStatus, setPartnerOfferStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [procurementStatus, setProcurementStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [procurementContactStatus, setProcurementContactStatus] = useState<"idle" | "loading" | "done" | "error">(
    "idle"
  );
  const [procurementExecutionNotifyStatus, setProcurementExecutionNotifyStatus] = useState<
    "idle" | "loading" | "done" | "error"
  >("idle");
  const [procurementPaymentStatus, setProcurementPaymentStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [refurbishmentStatus, setRefurbishmentStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [productInspectionReportStatus, setProductInspectionReportStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [ruleStatus, setRuleStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [insightStatus, setInsightStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [impactStatus, setImpactStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [carbonLedgerStatus, setCarbonLedgerStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [priceEvidenceStatus, setPriceEvidenceStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [pricingGuideStatus, setPricingGuideStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [intakeEventStatus, setIntakeEventStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [intakeQuoteStatus, setIntakeQuoteStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [notificationStatus, setNotificationStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [xianyuVisibleText, setXianyuVisibleText] = useState("");
  const [xianyuPreview, setXianyuPreview] = useState<XianyuVisibleSamplePreview | null>(null);
  const [xianyuImportResult, setXianyuImportResult] = useState<
    (XianyuVisibleImportResult | XianyuVisibleImportAndProcurementResult) | null
  >(null);

  async function refresh(nextToken = token) {
    if (!nextToken) {
      setStatus("idle");
      return;
    }

    setStatus("loading");
    try {
      const data = await loadAdminDashboard(nextToken);
      setOverview(data.overview);
      setUnitEconomics(data.unitEconomics);
      setReadiness(data.readiness);
      setAdminProducts(data.products);
      setInspectionTemplates(data.inspectionTemplates);
      setOrders(data.orders);
      setPaymentRecords(data.paymentRecords);
      setFulfillmentTasks(data.fulfillmentTasks);
      setServiceCases(data.serviceCases);
      setIntakeLeads(data.intakeLeads);
      setIntakeQuotes(data.intakeQuotes);
      setNotifications(data.notifications);
      setOpportunities(data.opportunities);
      setOpportunityImportRuns(data.opportunityImportRuns);
      setSupplierPartners(data.supplierPartners);
      setPartnerOffers(data.partnerOffers);
      setProcurementExecutionItems(data.procurementExecutionItems);
      setProcurementOrders(data.procurementOrders);
      setProcurementPaymentRecords(data.procurementPaymentRecords);
      setRefurbishmentTasks(data.refurbishmentTasks);
      setProductInspectionReports(data.productInspectionReports);
      setWatchRules(data.watchRules);
      setMarketInsights(data.marketInsights);
      setImpactMetrics(data.impactMetrics);
      setCarbonLedgerEntries(data.carbonLedgerEntries);
      setPriceEvidence(data.priceEvidence);
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }

  useEffect(() => {
    if (token) {
      void refresh(token);
    }
  }, [token]);

  function saveToken(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextToken = draftToken.trim();
    localStorage.setItem("yoyo-admin-token", nextToken);
    setToken(nextToken);
  }

  async function markNotificationRead(notification: AdminNotificationMessage) {
    if (!token || notification.channel !== "site" || notification.status === "read") {
      return;
    }

    setNotificationStatus("loading");
    try {
      await markAdminNotificationRead(token, notification.id);
      setNotificationStatus("done");
      await refresh();
    } catch {
      setNotificationStatus("error");
    }
  }

  async function advanceOrder(order: AdminOrder) {
    const nextStatus: Record<AdminOrder["status"], AdminOrder["status"]> = {
      new: "confirmed",
      confirmed: "paid",
      paid: "fulfilled",
      fulfilled: "fulfilled",
      cancelled: "cancelled"
    };
    await updateAdminOrderStatus(token, order.id, nextStatus[order.status]);
    await refresh();
  }

  async function submitPaymentRecord(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setPaymentStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    setPaymentStatus("loading");

    try {
      await createAdminPaymentRecord(token, {
        orderId: Number(formData.get("orderId")),
        paymentType: formData.get("paymentType") as PaymentType,
        amount: Number(formData.get("amount")),
        channel: formData.get("channel") as PaymentChannel,
        externalRef: String(formData.get("externalRef") ?? "").trim() || null,
        status: formData.get("status") as PaymentStatus,
        operatorNote: String(formData.get("operatorNote") ?? "")
      });
      setPaymentStatus("done");
      form.reset();
      await refresh();
    } catch {
      setPaymentStatus("error");
    }
  }

  async function submitFulfillmentTask(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setFulfillmentStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    const scheduledAt = String(formData.get("scheduledAt") ?? "").trim();
    setFulfillmentStatus("loading");

    try {
      await createAdminFulfillmentTask(token, {
        orderId: Number(formData.get("orderId")),
        taskType: formData.get("taskType") as FulfillmentTaskType,
        city: String(formData.get("city") ?? ""),
        address: String(formData.get("address") ?? ""),
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        assignee: String(formData.get("assignee") ?? ""),
        status: formData.get("status") as FulfillmentStatus,
        operatorNote: String(formData.get("operatorNote") ?? "")
      });
      setFulfillmentStatus("done");
      form.reset();
      await refresh();
    } catch {
      setFulfillmentStatus("error");
    }
  }

  async function advanceFulfillmentTask(task: AdminFulfillmentTask) {
    if (!token) {
      setFulfillmentStatus("error");
      return;
    }

    const nextStatus: Record<AdminFulfillmentTask["status"], AdminFulfillmentTask["status"]> = {
      new: "scheduled",
      scheduled: "in_transit",
      in_transit: "completed",
      completed: "completed",
      cancelled: "cancelled"
    };
    const status = nextStatus[task.status];

    setFulfillmentStatus("loading");
    try {
      await recordAdminFulfillmentEvent(token, task.id, {
        status,
        operatorNote: `${translateFulfillmentType(task.task_type)} · ${translateFulfillmentStatus(status)}`
      });
      await refresh();
      setFulfillmentStatus("done");
    } catch {
      setFulfillmentStatus("error");
    }
  }

  async function advanceServiceCase(serviceCase: AdminServiceCase) {
    if (!token) {
      setServiceCaseStatus("error");
      return;
    }

    const nextStatus: Record<AdminServiceCase["status"], AdminServiceCase["status"]> = {
      new: "in_review",
      in_review: "resolving",
      resolving: "resolved",
      resolved: "closed",
      closed: "closed"
    };
    const status = nextStatus[serviceCase.status];
    const eventType = status === "closed" ? "close" : status === "resolving" ? "repair" : "note";

    setServiceCaseStatus("loading");
    try {
      await recordAdminServiceCaseEvent(token, serviceCase.id, {
        status,
        eventType,
        operatorNote: `${translateServiceIssue(serviceCase.issue_type)} · ${translateServiceStatus(status)}`
      });
      await refresh();
      setServiceCaseStatus("done");
    } catch {
      setServiceCaseStatus("error");
    }
  }

  async function advanceIntake(lead: AdminIntakeLead) {
    const nextStatus: Record<AdminIntakeLead["status"], AdminIntakeLead["status"]> = {
      new: "quoted",
      quoted: "scheduled",
      scheduled: "closed",
      closed: "closed"
    };
    await updateAdminIntakeStatus(token, lead.id, nextStatus[lead.status]);
    await refresh();
  }

  async function submitIntakeEvent(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setIntakeEventStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    const amount = Number(formData.get("amount"));
    const scheduledAt = String(formData.get("scheduledAt") ?? "").trim();
    setIntakeEventStatus("loading");

    try {
      await recordAdminIntakeEvent(token, Number(formData.get("leadId")), {
        eventType: formData.get("eventType") as IntakeEventType,
        amount: Number.isFinite(amount) && amount > 0 ? amount : null,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        productId: String(formData.get("productId") ?? "").trim() || null,
        operatorNote: String(formData.get("operatorNote") ?? "")
      });
      setIntakeEventStatus("done");
      form.reset();
      await refresh();
    } catch {
      setIntakeEventStatus("error");
    }
  }

  async function submitIntakeQuote(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setIntakeQuoteStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    const expectedListPrice = Number(formData.get("expectedListPrice"));
    const validUntil = String(formData.get("validUntil") ?? "").trim();
    setIntakeQuoteStatus("loading");

    try {
      await createAdminIntakeQuote(token, {
        intakeLeadId: Number(formData.get("intakeLeadId")),
        quoteAmount: Number(formData.get("quoteAmount")),
        expectedListPrice: Number.isFinite(expectedListPrice) && expectedListPrice > 0 ? expectedListPrice : null,
        validUntil: validUntil ? new Date(validUntil).toISOString() : null,
        status: formData.get("status") as Exclude<IntakeQuoteStatus, "converted">,
        operatorNote: String(formData.get("operatorNote") ?? "")
      });
      setIntakeQuoteStatus("done");
      form.reset();
      await refresh();
    } catch {
      setIntakeQuoteStatus("error");
    }
  }

  async function changeIntakeQuoteStatus(quote: AdminIntakeQuote, status: Exclude<IntakeQuoteStatus, "converted">) {
    if (!token) {
      setIntakeQuoteStatus("error");
      return;
    }

    setIntakeQuoteStatus("loading");
    try {
      await updateAdminIntakeQuoteStatus(
        token,
        quote.id,
        status,
        `${quote.lead_title} · ${translateIntakeQuoteStatus(status)}`
      );
      await refresh();
      setIntakeQuoteStatus("done");
    } catch {
      setIntakeQuoteStatus("error");
    }
  }

  async function convertIntakeQuote(quote: AdminIntakeQuote) {
    if (!token) {
      setIntakeQuoteStatus("error");
      return;
    }

    setIntakeQuoteStatus("loading");
    try {
      await convertAdminIntakeQuoteToProcurement(token, quote.id, {
        paymentStatus: "unpaid",
        status: "new",
        operatorNote: `${quote.lead_title} · 报价转采购`
      });
      await refresh();
      setIntakeQuoteStatus("done");
    } catch {
      setIntakeQuoteStatus("error");
    }
  }

  async function changeProductStatus(product: AdminProduct, lifecycleStatus: ProductStatus) {
    if (!token) {
      setProductLifecycleStatus("error");
      return;
    }

    setProductLifecycleStatus("loading");
    try {
      await updateAdminProductStatus(
        token,
        product.id,
        lifecycleStatus,
        `${product.title} · ${translateProductStatus(lifecycleStatus)}`
      );
      await refresh();
      setProductLifecycleStatus("done");
    } catch {
      setProductLifecycleStatus("error");
    }
  }

  async function submitInspectionTemplate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setInspectionTemplateStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    const items = String(formData.get("items") ?? "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [label, severity = "required", requirement = "按模板完成检查"] = line.split("|").map((part) => part.trim());
        const normalizedSeverity: InspectionTemplateSeverity =
          severity === "recommended" || severity === "safety_critical" ? severity : "required";

        return {
          label,
          severity: normalizedSeverity,
          requirement
        };
      });

    setInspectionTemplateStatus("loading");
    try {
      await createAdminInspectionTemplate(token, {
        category: formData.get("category") === "baby" ? "baby" : "mobility",
        subcategory: String(formData.get("subcategory") ?? ""),
        templateName: String(formData.get("templateName") ?? ""),
        version: Number(formData.get("version") ?? 1),
        status: formData.get("status") as InspectionTemplateStatus,
        operatorNote: String(formData.get("operatorNote") ?? ""),
        items
      });
      setInspectionTemplateStatus("done");
      form.reset();
      await refresh();
    } catch {
      setInspectionTemplateStatus("error");
    }
  }

  async function changeInspectionTemplateStatus(template: AdminInspectionTemplate, status: InspectionTemplateStatus) {
    if (!token) {
      setInspectionTemplateStatus("error");
      return;
    }

    setInspectionTemplateStatus("loading");
    try {
      await updateAdminInspectionTemplateStatus(
        token,
        template.id,
        status,
        `${template.template_name} · ${translateInspectionTemplateStatus(status)}`
      );
      await refresh();
      setInspectionTemplateStatus("done");
    } catch {
      setInspectionTemplateStatus("error");
    }
  }

  async function previewXianyuVisibleSamples(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setXianyuVisibleStatus("error");
      return;
    }

    const form = event.currentTarget;
    setXianyuVisibleStatus("loading");
    setXianyuImportResult(null);

    try {
      const preview = await previewAdminXianyuVisibleSamples(
        token,
        xianyuVisibleText,
        readXianyuVisibleDefaults(new FormData(form))
      );
      setXianyuPreview(preview);
      setXianyuVisibleStatus("done");
    } catch {
      setXianyuVisibleStatus("error");
    }
  }

  async function importXianyuVisibleWorkbench(form: HTMLFormElement | null) {
    if (!token || !form || !xianyuPreview || xianyuPreview.summary.validRows === 0) {
      setXianyuVisibleStatus("error");
      return;
    }

    setXianyuVisibleStatus("loading");
    try {
      const result = await importAdminXianyuVisibleSampleText(
        token,
        xianyuVisibleText,
        readXianyuVisibleDefaults(new FormData(form))
      );
      setXianyuImportResult(result);
      setXianyuPreview(null);
      setXianyuVisibleText("");
      form.reset();
      await refresh();
      setXianyuVisibleStatus("done");
    } catch {
      setXianyuVisibleStatus("error");
    }
  }

  async function importXianyuVisibleAndProcure(form: HTMLFormElement | null) {
    if (!token || !form || !xianyuPreview || xianyuPreview.summary.validRows === 0) {
      setXianyuVisibleStatus("error");
      return;
    }

    setXianyuVisibleStatus("loading");
    try {
      const result = await importAdminXianyuVisibleSampleTextAndConvertToProcurement(
        token,
        xianyuVisibleText,
        readXianyuVisibleDefaults(new FormData(form))
      );
      setXianyuImportResult(result);
      setXianyuPreview(null);
      setXianyuVisibleText("");
      form.reset();
      await refresh();
      setXianyuVisibleStatus("done");
    } catch {
      setXianyuVisibleStatus("error");
    }
  }

  async function recordOpportunity(opportunity: AdminOpportunity, action: NonNullable<AdminOpportunity["latest_action"]>) {
    if (!token) {
      setOpportunityStatus("error");
      return;
    }

    setOpportunityStatus("loading");
    try {
      await recordAdminOpportunityAction(token, opportunity.id, action, `${opportunity.title} · ${opportunity.city}`);
      await refresh();
      setOpportunityStatus("done");
    } catch {
      setOpportunityStatus("error");
    }
  }

  async function convertOpportunity(opportunity: AdminOpportunity) {
    if (!token) {
      setOpportunityStatus("error");
      return;
    }

    setOpportunityStatus("loading");
    try {
      await convertAdminOpportunityToProcurement(token, opportunity.id, {
        quantity: 1,
        agreedPrice: Number(opportunity.ask_price),
        estimatedListPrice: Number(opportunity.fair_price),
        paymentStatus: "unpaid",
        status: "negotiating",
        operatorNote: `${opportunity.title} · ${opportunity.city} · 机会转采购`
      });
      await refresh();
      setOpportunityStatus("done");
    } catch {
      setOpportunityStatus("error");
    }
  }

  async function submitManualOpportunity(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setManualOpportunityStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    const fairPrice = Number(formData.get("fairPrice"));
    const publishedAt = String(formData.get("publishedAt") ?? "").trim();
    const sourceUrl = String(formData.get("sourceUrl") ?? "").trim();
    setManualOpportunityStatus("loading");

    try {
      await createAdminOpportunity(token, {
        id: String(formData.get("id") ?? "").trim() || undefined,
        title: String(formData.get("title") ?? ""),
        category: formData.get("category") === "baby" ? "baby" : "mobility",
        city: String(formData.get("city") ?? ""),
        askPrice: Number(formData.get("askPrice")),
        fairPrice,
        sellerSignal: String(formData.get("sellerSignal") ?? ""),
        publishedAt: publishedAt ? new Date(publishedAt).toISOString() : "",
        sourceUrl
      });
      setManualOpportunityStatus("done");
      form.reset();
      await refresh();
    } catch {
      setManualOpportunityStatus("error");
    }
  }

  async function submitSupplierPartner(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setSupplierPartnerStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    setSupplierPartnerStatus("loading");

    try {
      await createAdminSupplierPartner(token, {
        name: String(formData.get("name") ?? ""),
        partnerType: formData.get("partnerType") as SupplierPartnerType,
        city: String(formData.get("city") ?? ""),
        contactName: String(formData.get("contactName") ?? ""),
        contact: String(formData.get("contact") ?? ""),
        status: formData.get("status") as SupplierPartnerStatus,
        operatorNote: String(formData.get("operatorNote") ?? "")
      });
      setSupplierPartnerStatus("done");
      form.reset();
      await refresh();
    } catch {
      setSupplierPartnerStatus("error");
    }
  }

  async function changeSupplierPartnerStatus(partner: AdminSupplierPartner, status: SupplierPartnerStatus) {
    if (!token) {
      setSupplierPartnerStatus("error");
      return;
    }

    setSupplierPartnerStatus("loading");
    try {
      await updateAdminSupplierPartnerStatus(
        token,
        partner.id,
        status,
        `${partner.name} · ${translateSupplierPartnerStatus(status)}`
      );
      await refresh();
      setSupplierPartnerStatus("done");
    } catch {
      setSupplierPartnerStatus("error");
    }
  }

  async function submitPartnerOffer(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setPartnerOfferStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    setPartnerOfferStatus("loading");

    try {
      await createAdminPartnerOffer(token, {
        partnerId: Number(formData.get("partnerId")),
        category: formData.get("category") === "baby" ? "baby" : "mobility",
        subcategory: String(formData.get("subcategory") ?? ""),
        title: String(formData.get("title") ?? ""),
        quantity: Number(formData.get("quantity") ?? 1),
        targetPrice: Number(formData.get("targetPrice")),
        city: String(formData.get("city") ?? ""),
        status: formData.get("status") as PartnerOfferStatus,
        operatorNote: String(formData.get("operatorNote") ?? "")
      });
      setPartnerOfferStatus("done");
      form.reset();
      await refresh();
    } catch {
      setPartnerOfferStatus("error");
    }
  }

  async function advancePartnerOffer(offer: AdminPartnerOffer) {
    if (!token) {
      setPartnerOfferStatus("error");
      return;
    }

    const nextStatus: Record<PartnerOfferStatus, PartnerOfferStatus> = {
      new: "evaluating",
      evaluating: "accepted",
      accepted: "converted",
      rejected: "rejected",
      converted: "converted"
    };
    const status = nextStatus[offer.status];

    setPartnerOfferStatus("loading");
    try {
      await recordAdminPartnerOfferEvent(token, offer.id, {
        status,
        operatorNote: `${offer.partner_name} · ${translatePartnerOfferStatus(status)}`
      });
      await refresh();
      setPartnerOfferStatus("done");
    } catch {
      setPartnerOfferStatus("error");
    }
  }

  async function convertPartnerOffer(offer: AdminPartnerOffer) {
    if (!token) {
      setPartnerOfferStatus("error");
      return;
    }

    setPartnerOfferStatus("loading");
    try {
      await convertAdminPartnerOfferToProcurement(token, offer.id, {
        agreedPrice: Number(offer.target_price),
        estimatedListPrice: null,
        paymentStatus: "unpaid",
        status: "negotiating",
        operatorNote: `${offer.partner_name} · ${offer.title} · 合作供给转采购`
      });
      await refresh();
      setPartnerOfferStatus("done");
    } catch {
      setPartnerOfferStatus("error");
    }
  }

  async function submitProcurementOrder(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setProcurementStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    const estimatedListPrice = Number(formData.get("estimatedListPrice"));
    setProcurementStatus("loading");

    try {
      await createAdminProcurementOrder(token, {
        sourceType: formData.get("sourceType") as ProcurementSourceType,
        sourceRef: String(formData.get("sourceRef") ?? ""),
        category: formData.get("category") === "baby" ? "baby" : "mobility",
        title: String(formData.get("title") ?? ""),
        city: String(formData.get("city") ?? ""),
        quantity: Number(formData.get("quantity") ?? 1),
        agreedPrice: Number(formData.get("agreedPrice")),
        estimatedListPrice: Number.isFinite(estimatedListPrice) && estimatedListPrice > 0 ? estimatedListPrice : null,
        paymentStatus: formData.get("paymentStatus") as ProcurementPaymentStatus,
        status: formData.get("status") as ProcurementStatus,
        productId: String(formData.get("productId") ?? "").trim() || null,
        operatorNote: String(formData.get("operatorNote") ?? "")
      });
      setProcurementStatus("done");
      form.reset();
      await refresh();
    } catch {
      setProcurementStatus("error");
    }
  }

  async function submitProcurementPaymentRecord(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setProcurementPaymentStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    setProcurementPaymentStatus("loading");

    try {
      await createAdminProcurementPaymentRecord(token, {
        procurementOrderId: Number(formData.get("procurementOrderId")),
        amount: Number(formData.get("amount")),
        freightCost: Number(formData.get("freightCost") || 0),
        paidAt: String(formData.get("paidAt") ?? "").trim() || null,
        dealAt: String(formData.get("dealAt") ?? "").trim() || null,
        operatorNote: String(formData.get("operatorNote") ?? "")
      });
      setProcurementPaymentStatus("done");
      form.reset();
      await refresh();
    } catch {
      setProcurementPaymentStatus("error");
    }
  }

  function getProcurementContactForm(item: AdminProcurementExecutionItem) {
    return procurementContactForms[item.id] ?? defaultProcurementContactForm(item);
  }

  function updateProcurementContactForm(
    item: AdminProcurementExecutionItem,
    patch: Partial<ProcurementContactFormState>
  ) {
    setProcurementContactForms((current) => ({
      ...current,
      [item.id]: {
        ...(current[item.id] ?? defaultProcurementContactForm(item)),
        ...patch
      }
    }));
  }

  async function submitProcurementContactAttempt(item: AdminProcurementExecutionItem) {
    if (!token) {
      setProcurementContactStatus("error");
      return;
    }

    const form = getProcurementContactForm(item);
    const sellerResponse = form.sellerResponse.trim();
    const confirmedPrice = form.confirmedPrice.trim() ? Number(form.confirmedPrice) : null;
    const followUpDate = form.nextFollowUpAt ? new Date(form.nextFollowUpAt) : null;

    if (
      !sellerResponse ||
      (form.result === "price_confirmed" && (!confirmedPrice || confirmedPrice <= 0)) ||
      (followUpDate && Number.isNaN(followUpDate.getTime()))
    ) {
      setProcurementContactStatus("error");
      return;
    }

    setProcurementContactStatus("loading");
    try {
      await createAdminProcurementContactAttempt(token, item.id, {
        channel: form.channel,
        result: form.result,
        sellerResponse,
        confirmedPrice,
        pickupMethod: form.pickupMethod,
        sellerType: form.sellerType,
        sellerCredit: form.sellerCredit,
        sameSkuCount: Number(form.sameSkuCount) || 0,
        usesRealPhotos: form.usesRealPhotos,
        supportsVideo: form.supportsVideo,
        supportsEscrow: form.supportsEscrow,
        supportsInspection: form.supportsInspection,
        asksOffPlatformPayment: form.asksOffPlatformPayment,
        refusesVerification: form.refusesVerification,
        priceAnomaly: form.priceAnomaly,
        responseQuality: form.responseQuality,
        nextFollowUpAt: followUpDate ? followUpDate.toISOString() : null,
        riskFlags: parseProcurementRiskFlags(form.riskFlags),
        operatorNote: form.operatorNote.trim()
      });
      setProcurementContactForms((current) => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
      await refresh();
      setProcurementContactStatus("done");
    } catch {
      setProcurementContactStatus("error");
    }
  }

  async function notifyProcurementExecutionQueue() {
    if (!token) {
      setProcurementExecutionNotifyStatus("error");
      return;
    }

    setProcurementExecutionNotifyStatus("loading");
    try {
      await notifyAdminProcurementExecutionQueue(token, { limit: 50 });
      await refresh();
      setProcurementExecutionNotifyStatus("done");
    } catch {
      setProcurementExecutionNotifyStatus("error");
    }
  }

  async function submitRefurbishmentTask(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setRefurbishmentStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    const procurementOrderId = Number(formData.get("procurementOrderId"));
    const actualCost = Number(formData.get("actualCost"));
    setRefurbishmentStatus("loading");

    try {
      await createAdminRefurbishmentTask(token, {
        procurementOrderId: Number.isFinite(procurementOrderId) && procurementOrderId > 0 ? procurementOrderId : null,
        productId: String(formData.get("productId") ?? "").trim() || null,
        taskType: formData.get("taskType") as RefurbishmentTaskType,
        category: formData.get("category") === "baby" ? "baby" : "mobility",
        title: String(formData.get("title") ?? ""),
        city: String(formData.get("city") ?? ""),
        assignee: String(formData.get("assignee") ?? ""),
        estimatedCost: Number(formData.get("estimatedCost") ?? 0),
        actualCost: Number.isFinite(actualCost) && actualCost >= 0 ? actualCost : null,
        status: formData.get("status") as RefurbishmentStatus,
        operatorNote: String(formData.get("operatorNote") ?? "")
      });
      setRefurbishmentStatus("done");
      form.reset();
      await refresh();
    } catch {
      setRefurbishmentStatus("error");
    }
  }

  async function advanceRefurbishmentTask(task: AdminRefurbishmentTask) {
    if (!token) {
      setRefurbishmentStatus("error");
      return;
    }

    const nextStatus: Record<RefurbishmentStatus, RefurbishmentStatus> = {
      new: "in_progress",
      in_progress: "qc_passed",
      blocked: "in_progress",
      qc_passed: "completed",
      qc_failed: "in_progress",
      completed: "completed",
      cancelled: "cancelled"
    };
    const status = nextStatus[task.status];

    setRefurbishmentStatus("loading");
    try {
      await recordAdminRefurbishmentEvent(token, task.id, {
        status,
        actualCost: task.actual_cost,
        operatorNote: `${translateRefurbishmentType(task.task_type)} · ${translateRefurbishmentStatus(status)}`
      });
      await refresh();
      setRefurbishmentStatus("done");
    } catch {
      setRefurbishmentStatus("error");
    }
  }

  async function submitProductInspectionReport(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setProductInspectionReportStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    const templateId = Number(formData.get("templateId"));
    const refurbishmentTaskId = Number(formData.get("refurbishmentTaskId"));
    const items = String(formData.get("items") ?? "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [label, result = "pass", severity = "required", detail = "已完成正式质检"] = line
          .split("|")
          .map((part) => part.trim());
        const normalizedResult: InspectionItem["result"] =
          result === "watch" || result === "fail" ? result : "pass";
        const normalizedSeverity: InspectionTemplateSeverity =
          severity === "recommended" || severity === "safety_critical" ? severity : "required";

        return {
          label,
          result: normalizedResult,
          severity: normalizedSeverity,
          detail
        };
      });

    setProductInspectionReportStatus("loading");
    try {
      await createAdminProductInspectionReport(token, {
        productId: String(formData.get("productId") ?? "").trim(),
        templateId: Number.isFinite(templateId) && templateId > 0 ? templateId : null,
        refurbishmentTaskId: Number.isFinite(refurbishmentTaskId) && refurbishmentTaskId > 0 ? refurbishmentTaskId : null,
        category: formData.get("category") === "baby" ? "baby" : "mobility",
        inspector: String(formData.get("inspector") ?? ""),
        reportStatus: formData.get("reportStatus") as ProductInspectionStatus,
        safetyScore: Number(formData.get("safetyScore") ?? 0),
        summary: String(formData.get("summary") ?? ""),
        operatorNote: String(formData.get("operatorNote") ?? ""),
        items
      });
      setProductInspectionReportStatus("done");
      form.reset();
      await refresh();
    } catch {
      setProductInspectionReportStatus("error");
    }
  }

  async function changeProductInspectionReportStatus(
    report: AdminProductInspectionReport,
    reportStatus: ProductInspectionStatus
  ) {
    if (!token) {
      setProductInspectionReportStatus("error");
      return;
    }

    setProductInspectionReportStatus("loading");
    try {
      await updateAdminProductInspectionReportStatus(
        token,
        report.id,
        reportStatus,
        `${report.product_title} · ${translateProductInspectionStatus(reportStatus)}`
      );
      await refresh();
      setProductInspectionReportStatus("done");
    } catch {
      setProductInspectionReportStatus("error");
    }
  }

  async function submitImpactMetric(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setImpactStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    setImpactStatus("loading");

    try {
      await createAdminImpactMetric(token, {
        label: String(formData.get("label") ?? ""),
        value: String(formData.get("value") ?? ""),
        detail: String(formData.get("detail") ?? ""),
        sortOrder: Number(formData.get("sortOrder") ?? 0)
      });
      setImpactStatus("done");
      form.reset();
      await refresh();
    } catch {
      setImpactStatus("error");
    }
  }

  async function submitCarbonLedgerEntry(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setCarbonLedgerStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    const orderId = Number(formData.get("orderId"));
    const procurementOrderId = Number(formData.get("procurementOrderId"));
    const refurbishmentTaskId = Number(formData.get("refurbishmentTaskId"));
    const occurredAt = String(formData.get("occurredAt") ?? "").trim();
    setCarbonLedgerStatus("loading");

    try {
      await createAdminCarbonLedgerEntry(token, {
        entryType: formData.get("entryType") as CarbonLedgerEntryType,
        category: formData.get("category") === "baby" ? "baby" : "mobility",
        productId: String(formData.get("productId") ?? "").trim() || null,
        orderId: Number.isFinite(orderId) && orderId > 0 ? orderId : null,
        procurementOrderId: Number.isFinite(procurementOrderId) && procurementOrderId > 0 ? procurementOrderId : null,
        refurbishmentTaskId: Number.isFinite(refurbishmentTaskId) && refurbishmentTaskId > 0 ? refurbishmentTaskId : null,
        city: String(formData.get("city") ?? ""),
        carbonKg: Number(formData.get("carbonKg")),
        evidenceTitle: String(formData.get("evidenceTitle") ?? ""),
        evidenceUrl: String(formData.get("evidenceUrl") ?? "").trim() || null,
        accountingMethod: String(formData.get("accountingMethod") ?? ""),
        operatorNote: String(formData.get("operatorNote") ?? ""),
        occurredAt: occurredAt ? new Date(occurredAt).toISOString() : null
      });
      setCarbonLedgerStatus("done");
      form.reset();
      await refresh();
    } catch {
      setCarbonLedgerStatus("error");
    }
  }

  async function submitPriceEvidence(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setPriceEvidenceStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    const evidenceAt = String(formData.get("evidenceAt") ?? "").trim();
    setPriceEvidenceStatus("loading");

    try {
      await createAdminPriceEvidence(token, {
        category: formData.get("category") === "baby" ? "baby" : "mobility",
        subcategory: String(formData.get("subcategory") ?? ""),
        city: String(formData.get("city") ?? ""),
        condition: String(formData.get("condition") ?? ""),
        observedPrice: Number(formData.get("observedPrice")),
        source: formData.get("source") as PriceEvidenceSource,
        sourceTitle: String(formData.get("sourceTitle") ?? ""),
        sourceUrl: String(formData.get("sourceUrl") ?? "").trim() || null,
        evidenceAt: evidenceAt ? new Date(evidenceAt).toISOString() : null,
        operatorNote: String(formData.get("operatorNote") ?? "")
      });
      setPriceEvidenceStatus("done");
      form.reset();
      await refresh();
    } catch {
      setPriceEvidenceStatus("error");
    }
  }

  async function importXianyuPriceEvidence() {
    if (!token) {
      setPriceEvidenceStatus("error");
      return;
    }

    setPriceEvidenceStatus("loading");
    try {
      await importAdminXianyuPriceEvidence(token);
      await refresh();
      setPriceEvidenceStatus("done");
    } catch {
      setPriceEvidenceStatus("error");
    }
  }

  async function submitPricingGuide(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setPricingGuideStatus("error");
      return;
    }

    const formData = new FormData(event.currentTarget);
    setPricingGuideStatus("loading");

    try {
      const guide = await loadAdminPricingGuide(token, {
        category: formData.get("category") === "baby" ? "baby" : "mobility",
        subcategory: String(formData.get("subcategory") ?? ""),
        city: String(formData.get("city") ?? "").trim() || null,
        condition: String(formData.get("condition") ?? "").trim() || null
      });
      setPricingGuide(guide);
      setPricingGuideStatus("done");
    } catch {
      setPricingGuideStatus("error");
    }
  }

  async function submitWatchRule(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setRuleStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    setRuleStatus("loading");

    try {
      await createAdminWatchRule(token, {
        id: String(formData.get("id") ?? "").trim() || undefined,
        keyword: String(formData.get("keyword") ?? ""),
        category: formData.get("category") === "baby" ? "baby" : "mobility",
        brandKeywords: String(formData.get("brandKeywords") ?? ""),
        productKeywords: String(formData.get("productKeywords") ?? ""),
        maxPrice: Number(formData.get("maxPrice")),
        city: String(formData.get("city") ?? ""),
        minScore: Number(formData.get("minScore")),
        minMarginRate: Number(formData.get("minMarginRate") ?? 0),
        alertCooldownMinutes: Number(formData.get("alertCooldownMinutes") ?? 60),
        priority: String(formData.get("priority") ?? "watch") as "watch" | "hot" | "must_buy"
      });
      setRuleStatus("done");
      form.reset();
      await refresh();
    } catch {
      setRuleStatus("error");
    }
  }

  async function submitMarketInsight(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setInsightStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    setInsightStatus("loading");

    try {
      await createAdminMarketInsight(token, {
        id: String(formData.get("id") ?? "").trim() || undefined,
        competitor: String(formData.get("competitor") ?? ""),
        insightType: formData.get("insightType") as MarketInsight["insightType"],
        sourceTitle: String(formData.get("sourceTitle") ?? ""),
        sourceUrl: String(formData.get("sourceUrl") ?? ""),
        evidenceDate: String(formData.get("evidenceDate") ?? "").trim() || null,
        summary: String(formData.get("summary") ?? ""),
        implication: String(formData.get("implication") ?? "")
      });
      setInsightStatus("done");
      form.reset();
      await refresh();
    } catch {
      setInsightStatus("error");
    }
  }

  async function submitProduct(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      setProductStatus("error");
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    const tags = String(formData.get("tags") ?? "")
      .split(/[，,]/)
      .map((tag) => tag.trim())
      .filter(Boolean);
    const inspection = String(formData.get("inspection") ?? "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [label, result = "pass", detail = "已完成基础检查"] = line.split("|").map((part) => part.trim());
        const normalizedResult: InspectionItem["result"] =
          result === "watch" || result === "fail" ? result : "pass";

        return {
          label,
          result: normalizedResult,
          detail
        };
      });

    setProductStatus("loading");
    try {
      await createAdminProduct(token, {
        id: String(formData.get("id") ?? "").trim() || undefined,
        title: String(formData.get("title") ?? ""),
        category: formData.get("category") === "baby" ? "baby" : "mobility",
        subcategory: String(formData.get("subcategory") ?? ""),
        price: Number(formData.get("price")),
        originalPrice: Number(formData.get("originalPrice")),
        city: String(formData.get("city") ?? ""),
        condition: formData.get("condition") as Product["condition"],
        carbonKg: Number(formData.get("carbonKg")),
        safetyScore: Number(formData.get("safetyScore")),
        warrantyDays: Number(formData.get("warrantyDays")),
        image: String(formData.get("image") ?? ""),
        tags,
        inspection,
        lifecycleStatus: formData.get("lifecycleStatus") as ProductStatus
      });
      setProductStatus("done");
      form.reset();
      await refresh();
    } catch {
      setProductStatus("error");
    }
  }

  const highProcurementExecutionCount = procurementExecutionItems.filter((item) => item.priority === "high").length;
  const contactProcurementExecutionCount = procurementExecutionItems.filter(
    (item) => item.next_action === "contact_seller" || item.next_action === "confirm_payment_terms"
  ).length;
  const receiptProcurementExecutionCount = procurementExecutionItems.filter(
    (item) => item.next_action === "confirm_receipt"
  ).length;
  const openProcurementExecutionCount = procurementExecutionItems.filter((item) => item.next_action !== "closed").length;
  const canRecordProcurementContactAction = (item: AdminProcurementExecutionItem) =>
    item.next_action === "contact_seller" || item.next_action === "confirm_payment_terms";

  return (
    <div className="view-stack">
      <LoadingOverlay active={status === "loading"} label="正在读取真实业务数据" />
      <section className="ops-hero">
        <p className="eyebrow">运营驾驶舱</p>
        <h2>真实数据，今天就能处理</h2>
        <form className="ops-token-form" onSubmit={saveToken}>
          <label>
            <KeyRound size={16} />
            <span>Token</span>
            <input
              value={draftToken}
              onChange={(event) => setDraftToken(event.target.value)}
              placeholder="输入 ADMIN_API_TOKEN"
              type="password"
              autoComplete="off"
            />
          </label>
          <button className="primary-button" type="submit">
            连接运营台
          </button>
        </form>
        {status === "error" && <p className="form-message error">运营接口不可用或 token 不正确</p>}
      </section>

      {token && status === "ready" && (
        <TradeLedgerPanel token={token} onChanged={() => void refresh()} />
      )}

      {overview && (
        <section className="ops-metrics">
          <MetricTile label="商品" value={`${overview.products.count}`} detail={`${overview.products.carbon_kg}kg CO2e`} />
          <MetricTile
            label="质检"
            value={`${overview.inspectionTemplates.active_count}/${overview.inspectionTemplates.count}`}
            detail={`${overview.inspectionTemplates.item_count} 个标准项`}
          />
          <MetricTile
            label="报告"
            value={`${overview.productInspectionReports.passed_count}/${overview.productInspectionReports.count}`}
            detail={`风险 ${overview.productInspectionReports.risk_count}`}
          />
          <MetricTile label="机会" value={`${overview.opportunities.count}`} detail={`均分 ${Math.round(overview.opportunities.avg_score)}`} />
          <MetricTile
            label="同步"
            value={`${overview.opportunityImports.success_count}/${overview.opportunityImports.count}`}
            detail={`失败 ${overview.opportunityImports.failed_count}`}
          />
          <MetricTile
            label="通知"
            value={`${overview.notifications.unread_count}/${overview.notifications.count}`}
            detail={`微信失败 ${overview.notifications.failed_count}`}
          />
          <MetricTile
            label="伙伴"
            value={`${overview.supplierPartners.active_count}/${overview.supplierPartners.count}`}
            detail={formatPartnerOfferStatus(overview.partnerOffersByStatus)}
          />
          <MetricTile
            label="采购"
            value={`${overview.procurement.count}`}
            detail={`成本 ${currency.format(Number(overview.procurement.committed_cost ?? 0))}`}
          />
          <MetricTile
            label="采购付款"
            value={currency.format(Number(overview.procurementPayments.confirmed_paid ?? 0))}
            detail={`待确认 ${currency.format(Number(overview.procurementPayments.pending_paid ?? 0))}`}
          />
          <MetricTile
            label="整备"
            value={`${overview.refurbishment.count}`}
            detail={`实耗 ${currency.format(Number(overview.refurbishment.actual_cost ?? 0))}`}
          />
          <MetricTile label="订单" value={`${sumStatus(overview.ordersByStatus)}`} detail={formatStatus(overview.ordersByStatus)} />
          <MetricTile label="回收" value={`${sumStatus(overview.intakeByStatus)}`} detail={formatStatus(overview.intakeByStatus)} />
          <MetricTile
            label="报价"
            value={`${overview.intakeQuotes.converted_count}/${overview.intakeQuotes.count}`}
            detail={`接受 ${overview.intakeQuotes.accepted_count}`}
          />
          <MetricTile label="履约" value={`${sumStatus(overview.fulfillmentByStatus)}`} detail={formatFulfillmentStatus(overview.fulfillmentByStatus)} />
          <MetricTile label="售后" value={`${sumStatus(overview.serviceCasesByStatus)}`} detail={formatServiceStatus(overview.serviceCasesByStatus)} />
          <MetricTile
            label="收款"
            value={currency.format(Number(overview.payments.confirmed_income ?? 0))}
            detail={`退款 ${currency.format(Number(overview.payments.confirmed_refund ?? 0))}`}
          />
          <MetricTile
            label="碳账"
            value={`${Number(overview.carbonLedger.total_carbon_kg ?? 0)}kg`}
            detail={`${overview.carbonLedger.count} 条台账`}
          />
          <MetricTile
            label="价证"
            value={`${overview.priceEvidence.count}`}
            detail={`均价 ${currency.format(Number(overview.priceEvidence.avg_observed_price ?? 0))}`}
          />
        </section>
      )}

      <section className="ops-section unit-economics-section">
        <div className="ops-section-action">
          <div className="mini-section-head">
            <CircleDollarSign size={18} />
            <h3>经营洞察</h3>
          </div>
          <span className={unitEconomics && Number(unitEconomics.summary.contribution_margin ?? 0) < 0 ? "insight-pill warning" : "insight-pill positive"}>
            {unitEconomics ? formatMarginRate(unitEconomics.summary.contribution_margin_rate) : "真实聚合"}
          </span>
        </div>
        {unitEconomics ? (
          <>
          <div className="unit-economics-grid">
            <div>
              <span>确认收入</span>
              <strong>{currency.format(Number(unitEconomics.summary.confirmed_income ?? 0))}</strong>
              <small>退款 {currency.format(Number(unitEconomics.summary.confirmed_refund ?? 0))}</small>
            </div>
            <div>
              <span>采购成本</span>
              <strong>{currency.format(Number(unitEconomics.summary.procurement_cost ?? 0))}</strong>
              <small>站外补录货款 + 运费</small>
            </div>
            <div>
              <span>整备成本</span>
              <strong>{currency.format(Number(unitEconomics.summary.refurbishment_cost ?? 0))}</strong>
              <small>实际成本优先，未完成用预估</small>
            </div>
            <div>
              <span>贡献毛利</span>
              <strong>{currency.format(Number(unitEconomics.summary.contribution_margin ?? 0))}</strong>
              <small>{unitEconomics.summary.active_inventory_count} 件活跃库存 · 标价 {currency.format(Number(unitEconomics.summary.active_inventory_value ?? 0))}</small>
            </div>
          </div>

          <div className="unit-economics-columns">
            <div>
              <div className="mini-section-head">
                <TrendingUp size={18} />
                <h3>来源结构</h3>
              </div>
              {unitEconomics.sourceMix.length > 0 ? (
                <div className="ops-list">
                  {unitEconomics.sourceMix.slice(0, 4).map((source) => (
                    <article className="ops-row unit-source" key={source.source_type}>
                      <PackageCheck size={18} />
                      <div>
                        <strong>
                          {translateProcurementSource(source.source_type)} · {source.quantity} 件
                        </strong>
                        <span>
                          成本 {currency.format(Number(source.committed_cost ?? 0))} · 预估售价{" "}
                          {currency.format(Number(source.estimated_list_value ?? 0))}
                        </span>
                        <small>
                          到货 {source.received_quantity} · 上架 {source.listed_quantity} · 采购单 {source.order_count}
                        </small>
                      </div>
                      <span className="ops-rank">{source.listed_quantity}/{source.quantity}</span>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="ops-empty">暂无采购来源结构</p>
              )}
            </div>

            <div>
              <div className="mini-section-head">
                <Gauge size={18} />
                <h3>单品毛利</h3>
              </div>
              {unitEconomics.productRows.length > 0 ? (
                <div className="ops-list">
                  {unitEconomics.productRows.slice(0, 5).map((row) => (
                    <article
                      className={`ops-row unit-product ${Number(row.contribution_margin ?? 0) < 0 ? "risk" : "healthy"}`}
                      key={row.product_id}
                    >
                      <CircleDollarSign size={18} />
                      <div>
                        <strong>
                          {row.title} · {translateProductStatus(row.lifecycle_status)}
                        </strong>
                        <span>
                          收入 {currency.format(Number(row.confirmed_income ?? 0))} · 成本{" "}
                          {currency.format(Number(row.procurement_cost ?? 0) + Number(row.refurbishment_cost ?? 0))}
                        </span>
                        <small>
                          标价 {currency.format(Number(row.list_price ?? 0))} · 碳 {row.carbon_kg}kg ·{" "}
                          {formatMarginRate(row.contribution_margin_rate)}
                        </small>
                      </div>
                      <span className="ops-rank">{currency.format(Number(row.contribution_margin ?? 0))}</span>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="ops-empty">暂无可计算毛利的商品</p>
              )}
            </div>
          </div>
          </>
        ) : (
          <p className="ops-empty">连接真实运营接口后显示确认收入、采购成本、整备成本、贡献毛利、来源结构和单品毛利。</p>
        )}
      </section>

      {readiness && (
        <section className={`ops-readiness ${readiness.status}`}>
          <div className="ops-section-action">
            <div className="mini-section-head">
              <ShieldCheck size={18} />
              <h3>业务就绪度</h3>
            </div>
            <span>{translateReadinessStatus(readiness.status)}</span>
          </div>
          <div className="readiness-list">
            {readiness.checks.map((check) => (
              <div className="readiness-row" key={check.key}>
                <span className={`check-dot ${check.status === "pass" ? "pass" : check.status === "warn" ? "watch" : "fail"}`} />
                <div>
                  <strong>{check.label}</strong>
                  <small>{check.detail}</small>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="ops-section notification-center">
        <div className="ops-section-action">
          <div className="mini-section-head">
            <Bell size={18} />
            <h3>通知中心</h3>
          </div>
          <span className={notifications.some((item) => item.status === "failed") ? "insight-pill warning" : "insight-pill positive"}>
            {notifications.filter((item) => item.status === "unread").length} 未读
          </span>
        </div>
        {notifications.length > 0 ? (
          <div className="ops-list">
            {notifications.slice(0, 8).map((notification) => (
              <article
                className={`ops-row notification ${notification.severity} ${notification.status}`}
                key={notification.id}
              >
                <Bell size={18} />
                <div>
                  <strong>{notification.title}</strong>
                  <span>
                    {formatNotificationChannel(notification.channel)} · {formatNotificationStatus(notification.status)} ·{" "}
                    {formatDateTime(notification.createdAt)}
                  </span>
                  <small>{notification.body}</small>
                  {notification.deliveryDetail && <small>{notification.deliveryDetail}</small>}
                </div>
                <div className="notification-actions">
                  {notification.actionUrl && <a href={notification.actionUrl}>打开</a>}
                  {notification.channel === "site" && notification.status !== "read" && (
                    <button
                      type="button"
                      onClick={() => void markNotificationRead(notification)}
                      disabled={notificationStatus === "loading"}
                    >
                      已读
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无站内信或微信模板消息审计</p>
        )}
        {notificationStatus === "error" && <p className="form-message error">通知状态更新失败</p>}
      </section>

      <section className="ops-section">
        <div className="mini-section-head">
          <Leaf size={18} />
          <h3>绿色账本维护</h3>
        </div>
        <form className="ops-product-form" onSubmit={submitImpactMetric}>
          <div className="ops-form-grid">
            <label>
              <span>指标名称</span>
              <input name="label" placeholder="累计减排" required />
            </label>
            <label>
              <span>指标值</span>
              <input name="value" placeholder="128kg CO2e" required />
            </label>
          </div>
          <label>
            <span>说明</span>
            <input name="detail" placeholder="来自真实订单和质检记录" required />
          </label>
          <label>
            <span>排序</span>
            <input name="sortOrder" inputMode="numeric" placeholder="0" defaultValue="0" required />
          </label>
          <button className="primary-button" type="submit" disabled={impactStatus === "loading"}>
            {impactStatus === "loading" ? "保存中" : "保存指标"}
          </button>
          {impactStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              指标已写入数据库
            </p>
          )}
          {impactStatus === "error" && <p className="form-message error">指标保存失败，请检查 token 和字段</p>}
        </form>
        {impactMetrics.length > 0 ? (
          <div className="ops-list">
            {impactMetrics.slice(0, 5).map((metric) => (
              <article className="ops-row impact" key={metric.id}>
                <Leaf size={18} />
                <div>
                  <strong>{metric.label}</strong>
                  <span>{metric.value}</span>
                  <small>{metric.detail}</small>
                </div>
                <span className="ops-rank">{metric.sort_order}</span>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无绿色账本指标</p>
        )}
        <form className="ops-product-form" onSubmit={submitCarbonLedgerEntry}>
          <div className="mini-section-head">
            <ClipboardList size={18} />
            <h3>碳减排台账</h3>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>业务类型</span>
              <select name="entryType" defaultValue="reuse_sale" required>
                <option value="reuse_sale">复用成交</option>
                <option value="procurement">回收采买</option>
                <option value="refurbishment">整备延寿</option>
                <option value="repair">维修延寿</option>
                <option value="recycle">回收处置</option>
                <option value="manual_adjustment">人工调整</option>
              </select>
            </label>
            <label>
              <span>品类</span>
              <select name="category" defaultValue="mobility" required>
                <option value="mobility">低碳出行</option>
                <option value="baby">母婴耐用品</option>
              </select>
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>城市</span>
              <input name="city" placeholder="上海" required />
            </label>
            <label>
              <span>减排 kg</span>
              <input name="carbonKg" inputMode="numeric" placeholder="76" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>商品 ID</span>
              <input name="productId" placeholder="可留空" />
            </label>
            <label>
              <span>订单 ID</span>
              <input name="orderId" inputMode="numeric" placeholder="可留空" />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>采购单 ID</span>
              <input name="procurementOrderId" inputMode="numeric" placeholder="可留空" />
            </label>
            <label>
              <span>整备任务 ID</span>
              <input name="refurbishmentTaskId" inputMode="numeric" placeholder="可留空" />
            </label>
          </div>
          <label>
            <span>证据标题</span>
            <input name="evidenceTitle" placeholder="订单、质检或核算证据名称" required />
          </label>
          <label>
            <span>证据 URL</span>
            <input name="evidenceUrl" type="url" placeholder="可留空" />
          </label>
          <label>
            <span>核算方法</span>
            <input name="accountingMethod" placeholder="同类新品制造排放 - 整备/物流排放，版本与来源" required />
          </label>
          <div className="ops-form-grid">
            <label>
              <span>发生时间</span>
              <input name="occurredAt" type="datetime-local" />
            </label>
            <label>
              <span>备注</span>
              <input name="operatorNote" placeholder="证据说明或复核人" />
            </label>
          </div>
          <button className="primary-button" type="submit" disabled={carbonLedgerStatus === "loading"}>
            {carbonLedgerStatus === "loading" ? "保存中" : "保存碳台账"}
          </button>
          {carbonLedgerStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              碳台账已写入数据库
            </p>
          )}
          {carbonLedgerStatus === "error" && <p className="form-message error">碳台账保存失败，请检查关联 ID 和字段</p>}
        </form>
        {carbonLedgerEntries.length > 0 ? (
          <div className="ops-list">
            {carbonLedgerEntries.slice(0, 6).map((entry) => (
              <article className="ops-row carbon-ledger" key={entry.id}>
                <Leaf size={18} />
                <div>
                  <strong>
                    #{entry.id} · {translateCarbonLedgerType(entry.entry_type)} · {entry.carbon_kg}kg CO2e
                  </strong>
                  <span>
                    {entry.city} · {entry.category === "mobility" ? "低碳出行" : "母婴耐用品"}
                    {entry.product_title ? ` · ${entry.product_title}` : ""}
                  </span>
                  <small>{entry.evidence_title} · {entry.accounting_method}</small>
                  <small>{entry.operator_note || entry.procurement_title || entry.refurbishment_title || formatDateTime(entry.occurred_at ?? entry.created_at)}</small>
                </div>
                <span className="ops-rank">kg</span>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无逐笔碳减排台账</p>
        )}
      </section>

      <section className="ops-section">
        <div className="mini-section-head">
          <BarChart3 size={18} />
          <h3>市场竞品洞察</h3>
        </div>
        <form className="ops-product-form" onSubmit={submitMarketInsight}>
          <div className="ops-form-grid">
            <label>
              <span>洞察 ID</span>
              <input name="id" placeholder="留空自动生成" />
            </label>
            <label>
              <span>竞品</span>
              <input name="competitor" placeholder="瓜子 / 爱回收 / 转转" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>类型</span>
              <select name="insightType" defaultValue="inspection" required>
                <option value="inspection">质检</option>
                <option value="supply">供给</option>
                <option value="pricing">定价</option>
                <option value="fulfillment">履约</option>
                <option value="capital">资本</option>
                <option value="category">品类</option>
                <option value="growth">增长</option>
              </select>
            </label>
            <label>
              <span>证据日期</span>
              <input name="evidenceDate" type="date" />
            </label>
          </div>
          <label>
            <span>来源标题</span>
            <input name="sourceTitle" placeholder="官方财报 / 官网页面 / 政策文件" required />
          </label>
          <label>
            <span>来源 URL</span>
            <input name="sourceUrl" type="url" placeholder="https://..." required />
          </label>
          <label>
            <span>事实摘要</span>
            <textarea name="summary" placeholder="只记录可追溯事实，不写猜测" required />
          </label>
          <label>
            <span>YooY 应对</span>
            <textarea name="implication" placeholder="转化为可执行的差异化策略" required />
          </label>
          <button className="primary-button" type="submit" disabled={insightStatus === "loading"}>
            {insightStatus === "loading" ? "保存中" : "保存洞察"}
          </button>
          {insightStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              洞察已写入数据库
            </p>
          )}
          {insightStatus === "error" && <p className="form-message error">洞察保存失败，请检查来源和字段</p>}
        </form>
        {marketInsights.length > 0 ? (
          <div className="ops-list">
            {marketInsights.slice(0, 5).map((insight) => (
              <article className="ops-row insight" key={insight.id}>
                <BarChart3 size={18} />
                <div>
                  <strong>{insight.competitor} · {translateInsightType(insight.insight_type)}</strong>
                  <span>{insight.summary}</span>
                  <small>{insight.implication}</small>
                </div>
                <a href={insight.source_url} target="_blank" rel="noreferrer" aria-label={`查看 ${insight.competitor} 来源`}>
                  来源
                </a>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无竞品洞察</p>
        )}
      </section>

      <section className="ops-section">
        <div className="mini-section-head">
          <CircleDollarSign size={18} />
          <h3>价格证据台账</h3>
        </div>
        <form className="ops-product-form" onSubmit={submitPricingGuide}>
          <div className="ops-form-grid">
            <label>
              <span>品类</span>
              <select name="category" defaultValue="mobility" required>
                <option value="mobility">低碳出行</option>
                <option value="baby">母婴耐用品</option>
              </select>
            </label>
            <label>
              <span>子类</span>
              <input name="subcategory" placeholder="电动自行车 / 推车" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>城市</span>
              <input name="city" placeholder="可留空看全国证据" />
            </label>
            <label>
              <span>成色</span>
              <input name="condition" placeholder="可留空看全部成色" />
            </label>
          </div>
          <button className="primary-button" type="submit" disabled={pricingGuideStatus === "loading"}>
            {pricingGuideStatus === "loading" ? "计算中" : "生成报价建议"}
          </button>
          {pricingGuideStatus === "error" && <p className="form-message error">报价建议生成失败，请检查 token 和字段</p>}
        </form>
        <button className="primary-button" type="button" onClick={() => void importXianyuPriceEvidence()} disabled={priceEvidenceStatus === "loading"}>
          {priceEvidenceStatus === "loading" ? "同步中" : "同步闲鱼行情"}
        </button>
        {pricingGuide && (
          <article className={`pricing-guide ${pricingGuide.status}`}>
            {pricingGuide.status === "ready" ? (
              <>
                <div>
                  <span>建议回收价</span>
                  <strong>{currency.format(pricingGuide.recommendedBuyPrice ?? 0)}</strong>
                </div>
                <div>
                  <span>建议上架价</span>
                  <strong>{currency.format(pricingGuide.recommendedListPrice ?? 0)}</strong>
                </div>
                <small>
                  {pricingGuide.sampleCount} 条证据 · 均价 {currency.format(pricingGuide.avgObservedPrice ?? 0)} ·
                  区间 {currency.format(pricingGuide.minObservedPrice ?? 0)}-{currency.format(pricingGuide.maxObservedPrice ?? 0)} ·
                  {translatePricingConfidence(pricingGuide.confidence)}
                </small>
              </>
            ) : (
              <>
                <div>
                  <span>报价建议</span>
                  <strong>需要证据</strong>
                </div>
                <small>当前筛选没有真实价格证据，先录入来源明确的观察价再报价。</small>
              </>
            )}
          </article>
        )}
        <form className="ops-product-form" onSubmit={submitPriceEvidence}>
          <div className="ops-form-grid">
            <label>
              <span>品类</span>
              <select name="category" defaultValue="mobility" required>
                <option value="mobility">低碳出行</option>
                <option value="baby">母婴耐用品</option>
              </select>
            </label>
            <label>
              <span>子类</span>
              <input name="subcategory" placeholder="电动自行车 / 推车" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>城市</span>
              <input name="city" placeholder="上海" required />
            </label>
            <label>
              <span>成色</span>
              <input name="condition" placeholder="轻微使用 / 翻新质检" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>观察价</span>
              <input name="observedPrice" type="number" inputMode="decimal" step="0.01" placeholder="1280.00" required />
            </label>
            <label>
              <span>来源</span>
              <select name="source" defaultValue="manual" required>
                <option value="manual">人工</option>
                <option value="xianyu">闲鱼授权</option>
                <option value="partner">合作供给</option>
                <option value="competitor">竞品</option>
              </select>
            </label>
          </div>
          <label>
            <span>来源标题</span>
            <input name="sourceTitle" placeholder="授权线索标题、合作报价单或竞品页面" required />
          </label>
          <label>
            <span>来源 URL</span>
            <input name="sourceUrl" type="url" placeholder="https://..." />
          </label>
          <label>
            <span>证据时间</span>
            <input name="evidenceAt" type="datetime-local" />
          </label>
          <label>
            <span>备注</span>
            <input name="operatorNote" placeholder="影响报价的电池、磨损、配件、保修条件" />
          </label>
          <button className="primary-button" type="submit" disabled={priceEvidenceStatus === "loading"}>
            {priceEvidenceStatus === "loading" ? "保存中" : "保存价格证据"}
          </button>
          {priceEvidenceStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              价格证据已更新
            </p>
          )}
          {priceEvidenceStatus === "error" && <p className="form-message error">价格证据保存失败，请检查来源和字段</p>}
        </form>
        {priceEvidence.length > 0 ? (
          <div className="ops-list">
            {priceEvidence.slice(0, 6).map((item) => (
              <article className="ops-row price-evidence" key={item.id}>
                <CircleDollarSign size={18} />
                <div>
                  <strong>{item.subcategory} · {currency.format(item.observed_price)}</strong>
                  <span>
                    {item.city} · {item.item_condition} · {translatePriceEvidenceSource(item.source)}
                  </span>
                  <small>{item.source_title}</small>
                  {item.operator_note && <small>{item.operator_note}</small>}
                </div>
                {item.source_url ? (
                  <a href={item.source_url} target="_blank" rel="noreferrer" aria-label={`查看 ${item.source_title}`}>
                    来源
                  </a>
                ) : (
                  <span className="ops-rank">#{item.id}</span>
                )}
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无真实价格证据</p>
        )}
      </section>

      <section className="ops-section">
        <div className="mini-section-head">
          <Store size={18} />
          <h3>合作供给</h3>
        </div>
        <form className="ops-product-form" onSubmit={submitSupplierPartner}>
          <div className="ops-form-grid">
            <label>
              <span>伙伴名称</span>
              <input name="name" placeholder="门店 / 社区 / 机构名称" required />
            </label>
            <label>
              <span>类型</span>
              <select name="partnerType" defaultValue="bike_shop" required>
                <option value="bike_shop">出行门店</option>
                <option value="baby_store">母婴门店</option>
                <option value="community">社区团长</option>
                <option value="repair_shop">维修点</option>
                <option value="institution">机构</option>
                <option value="other">其他</option>
              </select>
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>城市</span>
              <input name="city" placeholder="上海" required />
            </label>
            <label>
              <span>状态</span>
              <select name="status" defaultValue="lead" required>
                <option value="lead">待跟进</option>
                <option value="active">活跃</option>
                <option value="paused">暂停</option>
                <option value="rejected">淘汰</option>
              </select>
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>联系人</span>
              <input name="contactName" placeholder="姓名可留空" />
            </label>
            <label>
              <span>联系方式</span>
              <input name="contact" type="tel" placeholder="手机号或微信" required />
            </label>
          </div>
          <label>
            <span>合作备注</span>
            <input name="operatorNote" placeholder="供给品类、验货方式、结算习惯" />
          </label>
          <button className="primary-button" type="submit" disabled={supplierPartnerStatus === "loading"}>
            {supplierPartnerStatus === "loading" ? "保存中" : "保存伙伴"}
          </button>
          {supplierPartnerStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              伙伴已写入数据库
            </p>
          )}
          {supplierPartnerStatus === "error" && <p className="form-message error">伙伴保存失败，请检查 token 和字段</p>}
        </form>
        {supplierPartners.length > 0 ? (
          <div className="ops-list">
            {supplierPartners.slice(0, 6).map((partner) => (
              <article className="ops-row supplier-partner" key={partner.id}>
                <Store size={18} />
                <div>
                  <strong>{partner.name}</strong>
                  <span>
                    {partner.city} · {translateSupplierPartnerType(partner.partner_type)} · {partner.contact_name || partner.contact}
                  </span>
                  <small>{translateSupplierPartnerStatus(partner.status)} · {partner.operator_note || "暂无备注"}</small>
                </div>
                <div className="ops-action-cluster" aria-label={`${partner.name} 伙伴状态`}>
                  <button
                    type="button"
                    onClick={() => void changeSupplierPartnerStatus(partner, "active")}
                    disabled={supplierPartnerStatus === "loading" || partner.status === "active"}
                  >
                    活跃
                  </button>
                  <button
                    type="button"
                    onClick={() => void changeSupplierPartnerStatus(partner, "lead")}
                    disabled={supplierPartnerStatus === "loading" || partner.status === "lead"}
                  >
                    跟进
                  </button>
                  <button
                    type="button"
                    onClick={() => void changeSupplierPartnerStatus(partner, "paused")}
                    disabled={supplierPartnerStatus === "loading" || partner.status === "paused"}
                  >
                    暂停
                  </button>
                  <button
                    type="button"
                    onClick={() => void changeSupplierPartnerStatus(partner, "rejected")}
                    disabled={supplierPartnerStatus === "loading" || partner.status === "rejected"}
                  >
                    淘汰
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无合作供给伙伴</p>
        )}
        <form className="ops-product-form" onSubmit={submitPartnerOffer}>
          <div className="ops-form-grid">
            <label>
              <span>供给伙伴</span>
              <select name="partnerId" defaultValue="" required>
                <option value="" disabled>
                  选择伙伴
                </option>
                {supplierPartners.map((partner) => (
                  <option value={partner.id} key={partner.id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>品类</span>
              <select name="category" defaultValue="mobility" required>
                <option value="mobility">低碳出行</option>
                <option value="baby">母婴耐用品</option>
              </select>
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>子类</span>
              <input name="subcategory" placeholder="电动自行车 / 推车" required />
            </label>
            <label>
              <span>城市</span>
              <input name="city" placeholder="上海" required />
            </label>
          </div>
          <label>
            <span>供给标题</span>
            <input name="title" placeholder="伙伴提供的批次或单品描述" required />
          </label>
          <div className="ops-form-grid">
            <label>
              <span>数量</span>
              <input name="quantity" inputMode="numeric" defaultValue="1" required />
            </label>
            <label>
              <span>目标收购价</span>
              <input name="targetPrice" type="number" inputMode="decimal" step="0.01" placeholder="980.00" required />
            </label>
          </div>
          <label>
            <span>报价状态</span>
            <select name="status" defaultValue="new" required>
              <option value="new">新供给</option>
              <option value="evaluating">验货中</option>
              <option value="accepted">已接受</option>
              <option value="rejected">已拒绝</option>
              <option value="converted">已转库存</option>
            </select>
          </label>
          <label>
            <span>供给备注</span>
            <input name="operatorNote" placeholder="成色、配件、批次风险、结算方式" />
          </label>
          <button className="primary-button" type="submit" disabled={partnerOfferStatus === "loading"}>
            {partnerOfferStatus === "loading" ? "登记中" : "登记供给"}
          </button>
          {partnerOfferStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              供给报价已写入数据库
            </p>
          )}
          {partnerOfferStatus === "error" && <p className="form-message error">供给登记失败，请检查伙伴和字段</p>}
        </form>
        {partnerOffers.length > 0 ? (
          <div className="ops-list">
            {partnerOffers.slice(0, 8).map((offer) => (
              <article className="ops-row partner-offer" key={offer.id}>
                <PackageCheck size={18} />
                <div>
                  <strong>{offer.title}</strong>
                  <span>
                    {offer.partner_name} · {offer.city} · {currency.format(offer.target_price)} × {offer.quantity}
                  </span>
                  <small>
                    {offer.category === "mobility" ? "低碳出行" : "母婴耐用品"} · {translatePartnerOfferStatus(offer.status)}
                  </small>
                  {offer.procurement_order_id && (
                    <small>
                      采购 #{offer.procurement_order_id} · {translateProcurementStatus(offer.procurement_status ?? "new")}
                    </small>
                  )}
                  <small>{offer.latest_note || offer.operator_note || "暂无跟进备注"}</small>
                </div>
                <div className="ops-action-cluster" aria-label={`${offer.title} 合作供给动作`}>
                  <button
                    type="button"
                    onClick={() => void advancePartnerOffer(offer)}
                    disabled={partnerOfferStatus === "loading" || offer.status === "converted" || offer.status === "rejected"}
                  >
                    推进
                  </button>
                  <button
                    className="ops-compact-action"
                    type="button"
                    onClick={() => void convertPartnerOffer(offer)}
                    disabled={partnerOfferStatus === "loading" || offer.status === "rejected" || Boolean(offer.procurement_order_id)}
                  >
                    转采购
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无合作供给报价</p>
        )}
      </section>

      <section className="ops-section procurement-execution-section">
        <div className="ops-section-action">
          <div className="mini-section-head">
            <CalendarCheck size={18} />
            <h3>采购执行队列</h3>
          </div>
          <div className="ops-section-action-inline">
            <span className={highProcurementExecutionCount > 0 ? "insight-pill warning" : "insight-pill positive"}>
              {highProcurementExecutionCount} 高优先
            </span>
            <button
              className="ops-small-button"
              type="button"
              onClick={() => void notifyProcurementExecutionQueue()}
              disabled={procurementExecutionNotifyStatus === "loading" || openProcurementExecutionCount === 0}
            >
              {procurementExecutionNotifyStatus === "loading" ? "提醒中" : "提醒队列"}
            </button>
          </div>
        </div>
        {procurementExecutionNotifyStatus === "done" && (
          <p className="form-message success">
            <CheckCircle2 size={16} />
            采购执行提醒已写入站内信
          </p>
        )}
        {procurementExecutionNotifyStatus === "error" && (
          <p className="form-message error">采购执行提醒失败，请检查 token 或稍后重试</p>
        )}
        {procurementContactStatus === "done" && (
          <p className="form-message success">
            <CheckCircle2 size={16} />
            卖家联系证据已保存
          </p>
        )}
        {procurementContactStatus === "error" && (
          <p className="form-message error">联系证据保存失败，请填写卖家回复；价格确认必须有确认价</p>
        )}
        <div className="execution-summary-grid">
          <article>
            <span>待处理</span>
            <strong>{openProcurementExecutionCount}</strong>
            <small>真实采购单派生</small>
          </article>
          <article>
            <span>待沟通</span>
            <strong>{contactProcurementExecutionCount}</strong>
            <small>联系、议价、验货</small>
          </article>
          <article>
            <span>待到货</span>
            <strong>{receiptProcurementExecutionCount}</strong>
            <small>已付款需验收</small>
          </article>
        </div>
        {procurementExecutionItems.length > 0 ? (
          <div className="ops-list">
            {procurementExecutionItems.slice(0, 8).map((item) => {
              const contactForm = getProcurementContactForm(item);
              const canRecordContact = canRecordProcurementContactAction(item);
              const estimatedListPrice = Number(item.estimated_list_price ?? 0);
              const estimatedMarginText =
                item.estimated_margin_cny === null ? "待估" : currency.format(item.estimated_margin_cny);
              const estimatedMarginRateText =
                item.estimated_margin_pct === null ? "待估" : `${Math.round(item.estimated_margin_pct * 100)}%`;

              return (
                <article className={`ops-row procurement-execution ${item.priority}`} key={item.id}>
                  <CalendarCheck size={18} />
                  <div>
                    <strong>#{item.id} · {item.title}</strong>
                    <span>
                      {translateCategory(item.category)} · {translateProcurementSource(item.source_type)} · {item.city}
                    </span>
                    <p className="procurement-product-intro">
                      商品说明：{item.title}
                      {item.product_title ? ` · 已关联库存 ${item.product_title}` : " · 待到货后生成一物一码库存"}
                    </p>
                    <div className="procurement-price-strip" aria-label={`${item.title} 的采购价格信息`}>
                      <span>
                        <small>采购成本</small>
                        <strong>{currency.format(item.total_cost_cny)}</strong>
                      </span>
                      <span>
                        <small>参考售价</small>
                        <strong>{estimatedListPrice > 0 ? currency.format(estimatedListPrice) : "待估"}</strong>
                      </span>
                      <span>
                        <small>预计毛利</small>
                        <strong>{estimatedMarginText}</strong>
                      </span>
                      <span>
                        <small>毛利率</small>
                        <strong>{estimatedMarginRateText}</strong>
                      </span>
                    </div>
                    <small>
                      {item.next_action_label} · {item.blocker}
                    </small>
                    {item.latest_contact_risk_level && (
                      <small className={`risk-review-line ${item.latest_contact_risk_level}`}>
                        {translateProcurementSellerRiskLevel(item.latest_contact_risk_level)}
                        {item.latest_contact_risk_score === null || item.latest_contact_risk_score === undefined
                          ? ""
                          : ` · ${Math.round(Number(item.latest_contact_risk_score))}分`}
                      </small>
                    )}
                    {(item.next_action === "prepare_payment" || item.next_action === "record_payment") && (
                      <form className="contact-evidence-panel" onSubmit={submitProcurementPaymentRecord}>
                        <input type="hidden" name="procurementOrderId" value={item.id} />
                        <div className="contact-evidence-grid">
                          <label>
                            <span>付款价格</span>
                            <input
                              name="amount"
                              inputMode="numeric"
                              defaultValue={String(item.agreed_price)}
                              required
                            />
                          </label>
                          <label>
                            <span>运费</span>
                            <input name="freightCost" type="number" inputMode="decimal" step="0.01" defaultValue="0.00" required />
                          </label>
                          <label>
                            <span>付款日期</span>
                            <input name="paidAt" type="datetime-local" defaultValue={toDateTimeLocalValue()} required />
                          </label>
                          <label>
                            <span>成交时间</span>
                            <input name="dealAt" type="datetime-local" defaultValue={toDateTimeLocalValue()} required />
                          </label>
                        </div>
                        <button className="ops-small-button" type="submit" disabled={procurementPaymentStatus === "loading"}>
                          {procurementPaymentStatus === "loading" ? "记录中" : "补录记账"}
                        </button>
                      </form>
                    )}
                    {canRecordContact && (
                      <div className="contact-evidence-panel" aria-label={`记录 ${item.title} 的卖家联系证据`}>
                        <div className="contact-evidence-grid">
                          <label>
                            <span>渠道</span>
                            <select
                              value={contactForm.channel}
                              onChange={(event) =>
                                updateProcurementContactForm(item, {
                                  channel: event.target.value as ProcurementContactChannel
                                })
                              }
                            >
                              {procurementContactChannelOptions.map((option) => (
                                <option value={option.value} key={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            <span>结果</span>
                            <select
                              value={contactForm.result}
                              onChange={(event) =>
                                updateProcurementContactForm(item, {
                                  result: event.target.value as ProcurementContactResult
                                })
                              }
                            >
                              {procurementContactResultOptions.map((option) => (
                                <option value={option.value} key={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            <span>确认价</span>
                            <input
                              type="number"
                              min="1"
                              inputMode="numeric"
                              value={contactForm.confirmedPrice}
                              onChange={(event) =>
                                updateProcurementContactForm(item, {
                                  confirmedPrice: event.target.value
                                })
                              }
                              placeholder="元"
                            />
                          </label>
                          <label>
                            <span>取货</span>
                            <select
                              value={contactForm.pickupMethod}
                              onChange={(event) =>
                                updateProcurementContactForm(item, {
                                  pickupMethod: event.target.value as ProcurementContactPickupMethod
                                })
                              }
                            >
                              {procurementPickupMethodOptions.map((option) => (
                                <option value={option.value} key={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            <span>卖家类型</span>
                            <select
                              value={contactForm.sellerType}
                              onChange={(event) =>
                                updateProcurementContactForm(item, {
                                  sellerType: event.target.value as ProcurementSellerType
                                })
                              }
                            >
                              {procurementSellerTypeOptions.map((option) => (
                                <option value={option.value} key={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            <span>信用</span>
                            <select
                              value={contactForm.sellerCredit}
                              onChange={(event) =>
                                updateProcurementContactForm(item, {
                                  sellerCredit: event.target.value as ProcurementSellerCredit
                                })
                              }
                            >
                              {procurementSellerCreditOptions.map((option) => (
                                <option value={option.value} key={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            <span>同款数量</span>
                            <input
                              type="number"
                              min="0"
                              inputMode="numeric"
                              value={contactForm.sameSkuCount}
                              onChange={(event) =>
                                updateProcurementContactForm(item, {
                                  sameSkuCount: event.target.value
                                })
                              }
                            />
                          </label>
                          <label>
                            <span>回复质量</span>
                            <select
                              value={contactForm.responseQuality}
                              onChange={(event) =>
                                updateProcurementContactForm(item, {
                                  responseQuality: event.target.value as ProcurementSellerResponseQuality
                                })
                              }
                            >
                              {procurementResponseQualityOptions.map((option) => (
                                <option value={option.value} key={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            <span>跟进时间</span>
                            <input
                              type="datetime-local"
                              value={contactForm.nextFollowUpAt}
                              onChange={(event) =>
                                updateProcurementContactForm(item, {
                                  nextFollowUpAt: event.target.value
                                })
                              }
                            />
                          </label>
                          <label>
                            <span>风险标签</span>
                            <input
                              value={contactForm.riskFlags}
                              onChange={(event) =>
                                updateProcurementContactForm(item, {
                                  riskFlags: event.target.value
                                })
                              }
                              placeholder="脱离平台付款、拒绝验货"
                            />
                          </label>
                          <div className="contact-risk-checks contact-evidence-wide" aria-label="采前风控项">
                            <label>
                              <input
                                type="checkbox"
                                checked={contactForm.usesRealPhotos}
                                onChange={(event) =>
                                  updateProcurementContactForm(item, {
                                    usesRealPhotos: event.target.checked
                                  })
                                }
                              />
                              <span>实拍图</span>
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={contactForm.supportsVideo}
                                onChange={(event) =>
                                  updateProcurementContactForm(item, {
                                    supportsVideo: event.target.checked
                                  })
                                }
                              />
                              <span>可补视频</span>
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={contactForm.supportsEscrow}
                                onChange={(event) =>
                                  updateProcurementContactForm(item, {
                                    supportsEscrow: event.target.checked
                                  })
                                }
                              />
                              <span>平台担保</span>
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={contactForm.supportsInspection}
                                onChange={(event) =>
                                  updateProcurementContactForm(item, {
                                    supportsInspection: event.target.checked
                                  })
                                }
                              />
                              <span>支持验货</span>
                            </label>
                            <label className="risk-negative">
                              <input
                                type="checkbox"
                                checked={contactForm.asksOffPlatformPayment}
                                onChange={(event) =>
                                  updateProcurementContactForm(item, {
                                    asksOffPlatformPayment: event.target.checked
                                  })
                                }
                              />
                              <span>私下付款</span>
                            </label>
                            <label className="risk-negative">
                              <input
                                type="checkbox"
                                checked={contactForm.refusesVerification}
                                onChange={(event) =>
                                  updateProcurementContactForm(item, {
                                    refusesVerification: event.target.checked
                                  })
                                }
                              />
                              <span>拒绝补证</span>
                            </label>
                            <label className="risk-negative">
                              <input
                                type="checkbox"
                                checked={contactForm.priceAnomaly}
                                onChange={(event) =>
                                  updateProcurementContactForm(item, {
                                    priceAnomaly: event.target.checked
                                  })
                                }
                              />
                              <span>价格异常</span>
                            </label>
                          </div>
                          <label className="contact-evidence-wide">
                            <span>卖家回复</span>
                            <textarea
                              value={contactForm.sellerResponse}
                              onChange={(event) =>
                                updateProcurementContactForm(item, {
                                  sellerResponse: event.target.value
                                })
                              }
                              placeholder="粘贴真实回复：还在、最低价、验货方式、可发货时间"
                              rows={2}
                            />
                          </label>
                          <label className="contact-evidence-wide">
                            <span>运营备注</span>
                            <input
                              value={contactForm.operatorNote}
                              onChange={(event) =>
                                updateProcurementContactForm(item, {
                                  operatorNote: event.target.value
                                })
                              }
                              placeholder="下一步：等卖家补图 / 付款前复核"
                            />
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="ops-action-cluster procurement-execution-actions">
                    {item.source_url && (
                      <a href={item.source_url} target="_blank" rel="noreferrer">
                        <ExternalLink size={14} />
                        打开闲鱼
                      </a>
                    )}
                    {canRecordContact && (
                      <button
                        type="button"
                        onClick={() => void submitProcurementContactAttempt(item)}
                        disabled={procurementContactStatus === "loading" || !contactForm.sellerResponse.trim()}
                      >
                        {procurementContactStatus === "loading" ? "保存中" : "保存联系"}
                      </button>
                    )}
                    <span className={`execution-priority ${item.priority}`}>
                      {translateProcurementExecutionPriority(item.priority)}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <p className="ops-empty">暂无采购执行动作</p>
        )}
      </section>

      <section className="ops-section">
        <div className="mini-section-head">
          <ClipboardList size={18} />
          <h3>采购入库</h3>
        </div>
        <form className="ops-product-form" onSubmit={submitProcurementOrder}>
          <div className="ops-form-grid">
            <label>
              <span>来源</span>
              <select name="sourceType" defaultValue="opportunity" required>
                <option value="opportunity">机会线索</option>
                <option value="partner_offer">合作报价</option>
                <option value="intake_lead">回收线索</option>
                <option value="manual">人工来源</option>
              </select>
            </label>
            <label>
              <span>来源编号</span>
              <input name="sourceRef" placeholder="机会 ID / 报价 # / 线索 #" />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>品类</span>
              <select name="category" defaultValue="mobility" required>
                <option value="mobility">低碳出行</option>
                <option value="baby">母婴耐用品</option>
              </select>
            </label>
            <label>
              <span>城市</span>
              <input name="city" placeholder="上海" required />
            </label>
          </div>
          <label>
            <span>采购标题</span>
            <input name="title" placeholder="准备采买或已采收的真实商品" required />
          </label>
          <div className="ops-form-grid">
            <label>
              <span>数量</span>
              <input name="quantity" inputMode="numeric" defaultValue="1" required />
            </label>
            <label>
              <span>约定采购价</span>
              <input name="agreedPrice" type="number" inputMode="decimal" step="0.01" placeholder="980.00" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>预估上架价</span>
              <input name="estimatedListPrice" type="number" inputMode="decimal" step="0.01" placeholder="1499.00" />
            </label>
            <label>
              <span>关联商品 ID</span>
              <input name="productId" placeholder="入库后商品 ID" />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>采购状态</span>
              <select name="status" defaultValue="new" required>
                <option value="new">新采购</option>
                <option value="negotiating">议价中</option>
                <option value="paid">已付款</option>
                <option value="received">已到货</option>
                <option value="listed">已上架</option>
                <option value="cancelled">已取消</option>
              </select>
            </label>
            <label>
              <span>付款状态</span>
              <select name="paymentStatus" defaultValue="unpaid" required>
                <option value="unpaid">未付款</option>
                <option value="partial">部分付款</option>
                <option value="paid">已付款</option>
                <option value="refunded">已退款</option>
              </select>
            </label>
          </div>
          <label>
            <span>采购备注</span>
            <input name="operatorNote" placeholder="验货条件、付款凭证、入库风险、毛利判断" />
          </label>
          <button className="primary-button" type="submit" disabled={procurementStatus === "loading"}>
            {procurementStatus === "loading" ? "记录中" : "记录采购单"}
          </button>
          {procurementStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              采购单已写入数据库
            </p>
          )}
          {procurementStatus === "error" && <p className="form-message error">采购单处理失败，请检查来源、价格和商品 ID</p>}
        </form>
        {procurementOrders.length > 0 ? (
          <div className="ops-list">
            {procurementOrders.slice(0, 8).map((order) => (
              <article className="ops-row procurement-order" key={order.id}>
                <ClipboardList size={18} />
                <div>
                  <strong>#{order.id} · {order.title}</strong>
                  <span>
                    {translateProcurementSource(order.source_type)} · {order.city} · 成本 {currency.format(order.agreed_price * order.quantity)}
                  </span>
                  <small>
                    {translateProcurementStatus(order.status)} · {translateProcurementPaymentStatus(order.payment_status)}
                    {order.product_title ? ` · ${order.product_title}` : ""}
                  </small>
                  <small>
                    已付{" "}
                    {currency.format(Math.max(Number(order.confirmed_paid ?? 0) - Number(order.confirmed_refund ?? 0), 0))} / 应付{" "}
                    {currency.format(order.agreed_price * order.quantity)}
                    {Number(order.pending_paid ?? 0) > 0 ? ` · 待确认 ${currency.format(Number(order.pending_paid))}` : ""}
                  </small>
                  <small>{order.latest_note || order.operator_note || "暂无采购备注"}</small>
                </div>
                <span className="ops-rank">{translateProcurementStatus(order.status)}</span>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无采购入库记录</p>
        )}
      </section>

      <section className="ops-section">
        <div className="mini-section-head">
          <CircleDollarSign size={18} />
          <h3>采购记账</h3>
        </div>
        <p className="ops-help">站外付款后补录。应用内不发起支付，只记付款价、运费、付款日期和成交时间。</p>
        <form className="ops-product-form" onSubmit={submitProcurementPaymentRecord}>
          <div className="ops-form-grid">
            <label>
              <span>采购单 ID</span>
              <input name="procurementOrderId" inputMode="numeric" placeholder="采购 # 编号" required />
            </label>
            <label>
              <span>付款价格</span>
              <input name="amount" type="number" inputMode="decimal" step="0.01" placeholder="实付货款，单位元" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>运费</span>
              <input name="freightCost" type="number" inputMode="decimal" step="0.01" placeholder="0.00" defaultValue="0.00" required />
            </label>
            <label>
              <span>付款日期</span>
              <input name="paidAt" type="datetime-local" defaultValue={toDateTimeLocalValue()} required />
            </label>
          </div>
          <label>
            <span>成交时间</span>
            <input name="dealAt" type="datetime-local" defaultValue={toDateTimeLocalValue()} required />
          </label>
          <label>
            <span>备注</span>
            <input name="operatorNote" placeholder="可留空" />
          </label>
          <button className="primary-button" type="submit" disabled={procurementPaymentStatus === "loading"}>
            {procurementPaymentStatus === "loading" ? "记录中" : "补录记账"}
          </button>
          {procurementPaymentStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              采购记账已写入
            </p>
          )}
          {procurementPaymentStatus === "error" && <p className="form-message error">采购记账失败，请检查采购单和金额</p>}
        </form>
        {procurementPaymentRecords.length > 0 ? (
          <div className="ops-list">
            {procurementPaymentRecords.slice(0, 8).map((payment) => (
              <article className="ops-row procurement-payment" key={payment.id}>
                <CircleDollarSign size={18} />
                <div>
                  <strong>
                    #{payment.procurement_order_id} · 货款 {currency.format(payment.amount)}
                    {Number(payment.freight_cost ?? 0) > 0 ? ` · 运费 ${currency.format(Number(payment.freight_cost))}` : ""}
                  </strong>
                  <span>
                    付款 {formatLedgerDateTime(payment.paid_at || payment.created_at)}
                    {payment.deal_at ? ` · 成交 ${formatLedgerDateTime(payment.deal_at)}` : ""}
                  </span>
                  <small>{payment.procurement_title}</small>
                  <small>{payment.operator_note || "站外付款补录"}</small>
                </div>
                <span className="ops-rank">#{payment.id}</span>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无采购记账</p>
        )}
      </section>

      <section className="ops-section">
        <div className="mini-section-head">
          <Wrench size={18} />
          <h3>整备质检</h3>
        </div>
        <form className="ops-product-form" onSubmit={submitRefurbishmentTask}>
          <div className="ops-form-grid">
            <label>
              <span>采购单 ID</span>
              <input name="procurementOrderId" inputMode="numeric" placeholder="可留空" />
            </label>
            <label>
              <span>商品 ID</span>
              <input name="productId" placeholder="上架前可留空" />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>任务类型</span>
              <select name="taskType" defaultValue="cleaning" required>
                <option value="cleaning">清洁消杀</option>
                <option value="repair">维修整备</option>
                <option value="battery_check">电池检测</option>
                <option value="safety_check">安全复检</option>
                <option value="photo">拍照建档</option>
                <option value="listing_prep">上架准备</option>
              </select>
            </label>
            <label>
              <span>品类</span>
              <select name="category" defaultValue="mobility" required>
                <option value="mobility">低碳出行</option>
                <option value="baby">母婴耐用品</option>
              </select>
            </label>
          </div>
          <label>
            <span>整备标题</span>
            <input name="title" placeholder="到货商品或待整备批次" required />
          </label>
          <div className="ops-form-grid">
            <label>
              <span>城市</span>
              <input name="city" placeholder="上海" required />
            </label>
            <label>
              <span>负责人</span>
              <input name="assignee" placeholder="技师 / 门店 / 运营" />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>预估成本</span>
              <input name="estimatedCost" type="number" inputMode="decimal" step="0.01" defaultValue="0.00" required />
            </label>
            <label>
              <span>实际成本</span>
              <input name="actualCost" type="number" inputMode="decimal" step="0.01" placeholder="完成后填写" />
            </label>
          </div>
          <label>
            <span>整备状态</span>
            <select name="status" defaultValue="new" required>
              <option value="new">待整备</option>
              <option value="in_progress">整备中</option>
              <option value="blocked">有阻塞</option>
              <option value="qc_passed">质检通过</option>
              <option value="qc_failed">质检未过</option>
              <option value="completed">已完成</option>
              <option value="cancelled">已取消</option>
            </select>
          </label>
          <label>
            <span>整备备注</span>
            <input name="operatorNote" placeholder="清洁、维修件、电池/安全复检、拍照和上架结论" />
          </label>
          <button className="primary-button" type="submit" disabled={refurbishmentStatus === "loading"}>
            {refurbishmentStatus === "loading" ? "安排中" : "安排整备任务"}
          </button>
          {refurbishmentStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              整备任务已写入数据库
            </p>
          )}
          {refurbishmentStatus === "error" && <p className="form-message error">整备任务处理失败，请检查采购单、商品 ID 和字段</p>}
        </form>
        {refurbishmentTasks.length > 0 ? (
          <div className="ops-list">
            {refurbishmentTasks.slice(0, 8).map((task) => (
              <article className="ops-row refurbishment-task" key={task.id}>
                <Wrench size={18} />
                <div>
                  <strong>#{task.id} · {translateRefurbishmentType(task.task_type)}</strong>
                  <span>
                    {task.title} · {task.city} · {task.assignee || "未分配"}
                  </span>
                  <small>
                    {translateRefurbishmentStatus(task.status)} · 预估 {currency.format(task.estimated_cost)}
                    {task.actual_cost ? ` · 实耗 ${currency.format(task.actual_cost)}` : ""}
                  </small>
                  <small>{task.latest_note || task.operator_note || task.product_title || task.procurement_title || "暂无整备备注"}</small>
                </div>
                <button
                  type="button"
                  onClick={() => void advanceRefurbishmentTask(task)}
                  disabled={refurbishmentStatus === "loading" || task.status === "completed" || task.status === "cancelled"}
                >
                  推进
                </button>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无整备质检任务</p>
        )}
      </section>

      <section className="ops-section">
        <div className="mini-section-head">
          <ShieldCheck size={18} />
          <h3>正式质检报告</h3>
        </div>
        <form className="ops-product-form" onSubmit={submitProductInspectionReport}>
          <div className="ops-form-grid">
            <label>
              <span>商品 ID</span>
              <input name="productId" placeholder="必须是已入库商品" required />
            </label>
            <label>
              <span>质检员</span>
              <input name="inspector" placeholder="门店 / 技师 / 运营" />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>模板 ID</span>
              <input name="templateId" inputMode="numeric" placeholder="可留空" />
            </label>
            <label>
              <span>整备任务 ID</span>
              <input name="refurbishmentTaskId" inputMode="numeric" placeholder="可留空" />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>品类</span>
              <select name="category" defaultValue="mobility" required>
                <option value="mobility">低碳出行</option>
                <option value="baby">母婴耐用品</option>
              </select>
            </label>
            <label>
              <span>报告状态</span>
              <select name="reportStatus" defaultValue="passed" required>
                <option value="passed">通过</option>
                <option value="draft">草稿</option>
                <option value="needs_repair">需返修</option>
                <option value="failed">未通过</option>
                <option value="archived">归档</option>
              </select>
            </label>
          </div>
          <label>
            <span>安全分</span>
            <input name="safetyScore" inputMode="numeric" defaultValue="95" required />
          </label>
          <label>
            <span>报告摘要</span>
            <input name="summary" placeholder="本次检测结论、风险点、质保建议" required />
          </label>
          <label>
            <span>检查项</span>
            <textarea
              name="items"
              placeholder={"电池健康|pass|safety_critical|容量、鼓包、充放电记录已核验\n制动系统|pass|required|刹车响应正常\n外观清洁|watch|recommended|轻微划痕已记录"}
              required
            />
          </label>
          <label>
            <span>备注</span>
            <input name="operatorNote" placeholder="报告来源、复检说明或用户可见承诺" />
          </label>
          <button className="primary-button" type="submit" disabled={productInspectionReportStatus === "loading"}>
            {productInspectionReportStatus === "loading" ? "保存中" : "保存质检报告"}
          </button>
          {productInspectionReportStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              质检报告已写入数据库并同步商品档案
            </p>
          )}
          {productInspectionReportStatus === "error" && <p className="form-message error">质检报告处理失败，请检查商品、模板、整备任务和字段</p>}
        </form>
        {productInspectionReports.length > 0 ? (
          <div className="ops-list">
            {productInspectionReports.slice(0, 8).map((report) => (
              <article className="ops-row product-inspection-report" key={report.id}>
                <ShieldCheck size={18} />
                <div>
                  <strong>#{report.id} · {report.product_title}</strong>
                  <span>
                    {report.category === "mobility" ? "低碳出行" : "母婴耐用品"} · 安全分 {report.safety_score} · {translateProductInspectionStatus(report.report_status)}
                  </span>
                  <small>{report.summary}</small>
                  <small>{report.item_count} 项 · {report.item_summary ?? "暂无检查项摘要"}</small>
                  <small>
                    {report.template_name
                      ? `${report.template_name} v${report.template_version ?? 1}`
                      : report.refurbishment_title
                        ? report.refurbishment_title
                        : report.latest_note || report.operator_note || "未关联模板或整备任务"}
                  </small>
                </div>
                <div className="ops-action-cluster" aria-label={`${report.product_title} 报告状态`}>
                  <button
                    type="button"
                    onClick={() => void changeProductInspectionReportStatus(report, "passed")}
                    disabled={productInspectionReportStatus === "loading" || report.report_status === "passed"}
                  >
                    通过
                  </button>
                  <button
                    type="button"
                    onClick={() => void changeProductInspectionReportStatus(report, "needs_repair")}
                    disabled={productInspectionReportStatus === "loading" || report.report_status === "needs_repair"}
                  >
                    返修
                  </button>
                  <button
                    type="button"
                    onClick={() => void changeProductInspectionReportStatus(report, "failed")}
                    disabled={productInspectionReportStatus === "loading" || report.report_status === "failed"}
                  >
                    未过
                  </button>
                  <button
                    type="button"
                    onClick={() => void changeProductInspectionReportStatus(report, "archived")}
                    disabled={productInspectionReportStatus === "loading" || report.report_status === "archived"}
                  >
                    归档
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无正式质检报告</p>
        )}
      </section>

      <section className="ops-section">
        <div className="ops-section-action">
          <div className="mini-section-head">
            <Radar size={18} />
            <h3>机会源采买</h3>
          </div>
        </div>
        {opportunityStatus === "done" && (
          <p className="form-message success">
            <CheckCircle2 size={16} />
            机会源已导入
          </p>
        )}
        {opportunityStatus === "error" && <p className="form-message error">刷新失败，请检查 token 和机会源配置</p>}
        <form className="ops-product-form sourcing-workbench" onSubmit={previewXianyuVisibleSamples}>
          <div className="ops-form-grid">
            <label>
              <span>品类</span>
              <select name="category" defaultValue="mobility" required>
                <option value="mobility">低碳出行</option>
                <option value="baby">母婴耐用品</option>
              </select>
            </label>
            <label>
              <span>城市</span>
              <input name="city" placeholder="上海" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>子类</span>
              <input name="subcategory" placeholder="儿童平衡车 / 婴儿推车" required />
            </label>
            <label>
              <span>成色</span>
              <input name="condition" placeholder="轻微使用 / 准新" required />
            </label>
          </div>
          <label>
            <span>闲鱼可见页样本</span>
            <textarea
              name="samples"
              value={xianyuVisibleText}
              onChange={(event) => {
                setXianyuVisibleText(event.target.value);
                setXianyuPreview(null);
                setXianyuImportResult(null);
                setXianyuVisibleStatus("idle");
              }}
              placeholder={"标题 | https://www.goofish.com/item?id=... | 328 | 520 | 同城可验\n或粘贴含标题、闲鱼链接、报价和参考价的可见页文本"}
              required
            />
          </label>
          <div className="sourcing-actions">
            <button className="primary-button" type="submit" disabled={xianyuVisibleStatus === "loading"}>
              {xianyuVisibleStatus === "loading" ? "处理中" : "预检样本"}
            </button>
            <button
              className="ops-small-button"
              type="button"
              onClick={(event) => void importXianyuVisibleWorkbench(event.currentTarget.form)}
              disabled={xianyuVisibleStatus === "loading" || !xianyuPreview || xianyuPreview.summary.validRows === 0}
            >
              导入有效
            </button>
            <button
              className="ops-small-button"
              type="button"
              onClick={(event) => void importXianyuVisibleAndProcure(event.currentTarget.form)}
              disabled={xianyuVisibleStatus === "loading" || !xianyuPreview || xianyuPreview.summary.validRows === 0}
            >
              导入并建采购
            </button>
          </div>
          {xianyuPreview && (
            <div className="sourcing-preview" aria-live="polite">
              <div className="sourcing-preview-grid">
                <div>
                  <span>总行</span>
                  <strong>{xianyuPreview.summary.totalRows}</strong>
                </div>
                <div>
                  <span>有效</span>
                  <strong>{xianyuPreview.summary.validRows}</strong>
                </div>
                <div>
                  <span>拒绝</span>
                  <strong>{xianyuPreview.summary.rejectedRows}</strong>
                </div>
              </div>
              {xianyuPreview.validSamples.length > 0 && (
                <div className="sourcing-preview-list">
                  {xianyuPreview.validSamples.slice(0, 4).map((sample) => (
                    <article className="sourcing-sample" key={`${sample.itemUrl}-${sample.askPrice}`}>
                      <CheckCircle2 size={16} />
                      <div>
                        <strong>{sample.title}</strong>
                        <span>
                          {sample.city} · {currency.format(sample.askPrice)}
                          {sample.fairPrice ? ` / 参考 ${currency.format(sample.fairPrice)}` : ""}
                        </span>
                        <small>{sample.itemUrl}</small>
                      </div>
                    </article>
                  ))}
                </div>
              )}
              {xianyuPreview.rejectedRows.length > 0 && (
                <div className="sourcing-rejected">
                  {xianyuPreview.rejectedRows.slice(0, 4).map((row) => (
                    <article key={`${row.rowNumber}-${row.reason}`}>
                      <strong>第 {row.rowNumber} 行 · {translateXianyuVisibleRejectReason(row.reason)}</strong>
                      <small>{row.rawText}</small>
                    </article>
                  ))}
                </div>
              )}
            </div>
          )}
          {xianyuImportResult && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              已导入 {xianyuImportResult.importedOpportunities} 个机会 · {xianyuImportResult.importedPriceEvidence} 条价证 · 通知 {xianyuImportResult.alerted}
              {"procurementOrders" in xianyuImportResult
                ? ` · 采购 ${xianyuImportResult.procurementOrders.length}`
                : ""}
            </p>
          )}
          {xianyuVisibleStatus === "done" && xianyuPreview && !xianyuImportResult && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              预检完成，可导入有效样本
            </p>
          )}
          {xianyuVisibleStatus === "error" && <p className="form-message error">闲鱼样本处理失败，请检查链接、价格、默认字段或拒绝原因</p>}
        </form>
        {opportunityImportRuns.length > 0 ? (
          <div className="ops-list">
            {opportunityImportRuns.slice(0, 4).map((run) => (
              <article className="ops-row import-run" key={run.id}>
                <Radar size={18} />
                <div>
                  <strong>
                    #{run.id} · {translateImportStatus(run.status)} · {translateImportTrigger(run.triggered_by)}
                  </strong>
                  <span>
                    {run.source_label} · 导入 {run.imported_count} · 提醒 {run.alerted_count}
                  </span>
                  <small>{run.error_message || formatDateTime(run.finished_at ?? run.started_at)}</small>
                </div>
                <span className={`ops-rank ${run.status}`}>{translateImportShortStatus(run.status)}</span>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无机会源同步记录</p>
        )}
        <form className="ops-product-form" onSubmit={submitManualOpportunity}>
          <div className="ops-form-grid">
            <label>
              <span>机会 ID</span>
              <input name="id" placeholder="留空自动生成" />
            </label>
            <label>
              <span>标题</span>
              <input name="title" placeholder="授权发现的好价商品" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>品类</span>
              <select name="category" defaultValue="mobility" required>
                <option value="mobility">低碳出行</option>
                <option value="baby">母婴耐用品</option>
              </select>
            </label>
            <label>
              <span>城市</span>
              <input name="city" placeholder="上海" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>报价</span>
              <input name="askPrice" type="number" inputMode="decimal" step="0.01" placeholder="980.00" required />
            </label>
            <label>
              <span>参考卖价</span>
              <input name="fairPrice" type="number" inputMode="decimal" step="0.01" placeholder="1650.00" required />
            </label>
          </div>
          <label>
            <span>卖家信号</span>
            <input name="sellerSignal" placeholder="同城可验，描述完整，信用良好" required />
          </label>
          <label>
            <span>平台商品链接</span>
            <input name="sourceUrl" type="url" placeholder="https://www.goofish.com/item?id=..." required />
          </label>
          <label>
            <span>发布时间</span>
            <input name="publishedAt" type="datetime-local" required />
          </label>
          <button className="primary-button" type="submit" disabled={manualOpportunityStatus === "loading"}>
            {manualOpportunityStatus === "loading" ? "入库中" : "人工机会入库"}
          </button>
          {manualOpportunityStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              机会已评分并写入数据库
            </p>
          )}
          {manualOpportunityStatus === "error" && <p className="form-message error">机会入库失败，请检查价格和字段</p>}
        </form>
        {opportunities.length > 0 ? (
          <div className="ops-list">
            {opportunities.map((opportunity) => {
              const sourceUrl = resolveAdminOpportunitySourceUrl(opportunity);

              return (
                <article className="ops-row opportunity" key={opportunity.id}>
                  <Radar size={18} />
                  <div>
                    <strong>{opportunity.title}</strong>
                    <span>
                      {opportunity.city} · {currency.format(opportunity.ask_price)} / 参考 {currency.format(opportunity.fair_price)}
                    </span>
                    <small>
                      分数 {opportunity.score} · {translateOpportunityAction(opportunity.latest_action ?? opportunity.action)}
                    </small>
                    {opportunity.procurement_order_id && (
                      <small>
                        采购 #{opportunity.procurement_order_id} · {translateProcurementStatus(opportunity.procurement_status ?? "new")}
                      </small>
                    )}
                  </div>
                  <div className="ops-action-cluster" aria-label={`${opportunity.title} 机会动作`}>
                    {sourceUrl ? (
                      <a href={sourceUrl} target="_blank" rel="noreferrer">
                        <ExternalLink size={14} />
                        原商品
                      </a>
                    ) : (
                      <span className="ops-link-missing">缺源链接</span>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        void recordOpportunity(
                          opportunity,
                          opportunity.action === "bid"
                            ? "contacted"
                            : opportunity.action === "inspect"
                              ? "inspection_scheduled"
                              : "watch"
                        )
                      }
                      disabled={opportunityStatus === "loading"}
                    >
                      处理
                    </button>
                    <button
                      className="ops-compact-action"
                      type="button"
                      onClick={() => void convertOpportunity(opportunity)}
                      disabled={opportunityStatus === "loading" || Boolean(opportunity.procurement_order_id)}
                    >
                      转采购
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <p className="ops-empty">暂无机会线索</p>
        )}
      </section>

      <section className="ops-section">
        <div className="mini-section-head">
          <ClipboardCheck size={18} />
          <h3>监控规则</h3>
        </div>
        <form className="ops-product-form" onSubmit={submitWatchRule}>
          <label>
            <span>规则 ID</span>
            <input name="id" placeholder="留空自动生成" />
          </label>
          <label>
            <span>关键词</span>
            <input name="keyword" placeholder="九号 电动车 电池健康" required />
          </label>
          <div className="ops-form-grid">
            <label>
              <span>品牌词</span>
              <input name="brandKeywords" placeholder="九号,雅迪,BABYZEN" />
            </label>
            <label>
              <span>产品词</span>
              <input name="productKeywords" placeholder="锂电,48V,YOYO2" />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>品类</span>
              <select name="category" defaultValue="mobility" required>
                <option value="mobility">低碳出行</option>
                <option value="baby">母婴耐用品</option>
              </select>
            </label>
            <label>
              <span>城市</span>
              <input name="city" placeholder="上海" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>最高价</span>
              <input name="maxPrice" type="number" inputMode="decimal" step="0.01" placeholder="1400.00" required />
            </label>
            <label>
              <span>最低分</span>
              <input name="minScore" inputMode="numeric" placeholder="82" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>最低毛利率</span>
              <input name="minMarginRate" inputMode="decimal" placeholder="0.25" defaultValue="0.18" required />
            </label>
            <label>
              <span>热推冷却分钟</span>
              <input name="alertCooldownMinutes" inputMode="numeric" placeholder="60" defaultValue="60" required />
            </label>
          </div>
          <label>
            <span>优先级</span>
            <select name="priority" defaultValue="hot" required>
              <option value="watch">关注</option>
              <option value="hot">热推</option>
              <option value="must_buy">必抢</option>
            </select>
          </label>
          <button className="primary-button" type="submit" disabled={ruleStatus === "loading"}>
            {ruleStatus === "loading" ? "保存中" : "保存规则"}
          </button>
          {ruleStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              规则已写入数据库
            </p>
          )}
          {ruleStatus === "error" && <p className="form-message error">规则保存失败</p>}
        </form>
        {watchRules.length > 0 && (
          <div className="ops-list">
            {watchRules.slice(0, 5).map((rule) => (
              <article className="ops-row" key={rule.id}>
                <ClipboardCheck size={18} />
                <div>
                  <strong>{rule.keyword}</strong>
                  <span>{formatWatchPriority(rule.priority)} · {rule.city} · 最高 {currency.format(rule.max_price)}</span>
                  <small>
                    {rule.category === "mobility" ? "低碳出行" : "母婴耐用品"} · {rule.min_score} 分起 ·{" "}
                    {formatMarginRate(rule.min_margin_rate)}
                  </small>
                  {(rule.brand_keywords || rule.product_keywords) && (
                    <small>
                      {rule.brand_keywords ? `品牌 ${rule.brand_keywords}` : ""}
                      {rule.brand_keywords && rule.product_keywords ? " · " : ""}
                      {rule.product_keywords ? `产品 ${rule.product_keywords}` : ""}
                    </small>
                  )}
                </div>
                <button type="button" disabled>
                  已启用
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="ops-section">
        <div className="mini-section-head">
          <PackageCheck size={18} />
          <h3>商品上架 / 质检维护</h3>
        </div>
        <form className="ops-product-form" onSubmit={submitInspectionTemplate}>
          <div className="ops-form-grid">
            <label>
              <span>模板名称</span>
              <input name="templateName" placeholder="电动自行车基础安全" required />
            </label>
            <label>
              <span>版本</span>
              <input name="version" inputMode="numeric" defaultValue="1" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>品类</span>
              <select name="category" defaultValue="mobility" required>
                <option value="mobility">低碳出行</option>
                <option value="baby">母婴耐用品</option>
              </select>
            </label>
            <label>
              <span>子类</span>
              <input name="subcategory" placeholder="电动自行车 / 安全座椅" required />
            </label>
          </div>
          <label>
            <span>模板状态</span>
            <select name="status" defaultValue="active" required>
              <option value="active">启用</option>
              <option value="draft">草稿</option>
              <option value="archived">归档</option>
            </select>
          </label>
          <label>
            <span>标准检查项</span>
            <textarea
              name="items"
              placeholder={"电池健康|safety_critical|容量、鼓包、充放电记录必须核验\n制动系统|required|刹车响应和线材磨损必须检查\n外观清洁|recommended|记录可见磨损和清洁状态"}
              required
            />
          </label>
          <label>
            <span>备注</span>
            <input name="operatorNote" placeholder="适用城市、门店、版本调整原因" />
          </label>
          <button className="primary-button" type="submit" disabled={inspectionTemplateStatus === "loading"}>
            {inspectionTemplateStatus === "loading" ? "保存中" : "保存质检模板"}
          </button>
          {inspectionTemplateStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              质检模板已写入数据库
            </p>
          )}
          {inspectionTemplateStatus === "error" && <p className="form-message error">质检模板保存失败，请检查模板项和 token</p>}
        </form>
        {inspectionTemplates.length > 0 ? (
          <div className="ops-list">
            {inspectionTemplates.slice(0, 6).map((template) => (
              <article className="ops-row inspection-template" key={template.id}>
                <ClipboardList size={18} />
                <div>
                  <strong>{template.template_name} v{template.version}</strong>
                  <span>
                    {template.category === "mobility" ? "低碳出行" : "母婴耐用品"} · {template.subcategory} · {template.item_count} 项
                  </span>
                  <small>{translateInspectionTemplateStatus(template.status)} · {template.item_summary ?? "暂无检查项摘要"}</small>
                </div>
                <div className="ops-action-cluster" aria-label={`${template.template_name} 模板状态`}>
                  <button
                    type="button"
                    onClick={() => void changeInspectionTemplateStatus(template, "active")}
                    disabled={inspectionTemplateStatus === "loading" || template.status === "active"}
                  >
                    启用
                  </button>
                  <button
                    type="button"
                    onClick={() => void changeInspectionTemplateStatus(template, "draft")}
                    disabled={inspectionTemplateStatus === "loading" || template.status === "draft"}
                  >
                    草稿
                  </button>
                  <button
                    type="button"
                    onClick={() => void changeInspectionTemplateStatus(template, "archived")}
                    disabled={inspectionTemplateStatus === "loading" || template.status === "archived"}
                  >
                    归档
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无标准质检模板</p>
        )}
        <form className="ops-product-form" onSubmit={submitProduct}>
          <label>
            <span>商品 ID</span>
            <input name="id" placeholder="留空自动生成" />
          </label>
          <label>
            <span>标题</span>
            <input name="title" placeholder="如 九号 B30C 精选车" required />
          </label>
          <div className="ops-form-grid">
            <label>
              <span>品类</span>
              <select name="category" defaultValue="mobility" required>
                <option value="mobility">低碳出行</option>
                <option value="baby">母婴耐用品</option>
              </select>
            </label>
            <label>
              <span>子类</span>
              <input name="subcategory" placeholder="电动自行车" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>售价</span>
              <input name="price" type="number" inputMode="decimal" step="0.01" placeholder="1499.00" required />
            </label>
            <label>
              <span>原价</span>
              <input name="originalPrice" type="number" inputMode="decimal" step="0.01" placeholder="2599.00" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>城市</span>
              <input name="city" placeholder="上海" required />
            </label>
            <label>
              <span>成色</span>
              <select name="condition" defaultValue="翻新质检" required>
                <option value="准新">准新</option>
                <option value="轻微使用">轻微使用</option>
                <option value="翻新质检">翻新质检</option>
                <option value="待整备">待整备</option>
              </select>
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>碳减排</span>
              <input name="carbonKg" inputMode="numeric" placeholder="76" required />
            </label>
            <label>
              <span>安全分</span>
              <input name="safetyScore" inputMode="numeric" placeholder="96" required />
            </label>
          </div>
          <label>
            <span>质保天数</span>
            <input name="warrantyDays" inputMode="numeric" placeholder="180" required />
          </label>
          <label>
            <span>库存状态</span>
            <select name="lifecycleStatus" defaultValue="listed" required>
              <option value="listed">上架可售</option>
              <option value="draft">草稿</option>
              <option value="reserved">已预留</option>
              <option value="sold">已成交</option>
              <option value="archived">已下架</option>
            </select>
          </label>
          <label>
            <span>图片 URL</span>
            <input name="image" type="url" placeholder="https://..." required />
          </label>
          <label>
            <span>标签</span>
            <input name="tags" placeholder="电池检测,同城自提,质保" required />
          </label>
          <label>
            <span>质检项</span>
            <textarea
              name="inspection"
              placeholder={"电池健康|pass|容量保持率 91%\n制动系统|pass|刹车校准完成\n外观|watch|轻微使用痕迹"}
              required
            />
          </label>
          <button className="primary-button" type="submit" disabled={productStatus === "loading"}>
            {productStatus === "loading" ? "保存中" : "保存商品"}
          </button>
          {productStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              商品已写入数据库
            </p>
          )}
          {productStatus === "error" && <p className="form-message error">保存失败，请检查 token 和字段</p>}
        </form>
        {productLifecycleStatus === "done" && (
          <p className="form-message success">
            <CheckCircle2 size={16} />
            库存状态已更新
          </p>
        )}
        {productLifecycleStatus === "error" && <p className="form-message error">库存状态更新失败</p>}
        {adminProducts.length > 0 ? (
          <div className="ops-list">
            {adminProducts.slice(0, 8).map((product) => (
              <article className="ops-row product-life" key={product.id}>
                <PackageCheck size={18} />
                <div>
                  <strong>{product.title}</strong>
                  <span>
                    {product.city} · {currency.format(product.price)} · {product.check_count} 项质检
                  </span>
                  <small>{translateProductStatus(product.lifecycle_status)} · {product.lifecycle_note ?? "暂无状态备注"}</small>
                  {product.public_url && (
                    <a className="ops-inline-link" href={product.public_url} target="_blank" rel="noreferrer">
                      <QrCode size={14} />
                      {product.item_code}
                    </a>
                  )}
                  {product.qr_url && (
                    <a className="ops-inline-link" href={product.qr_url} target="_blank" rel="noreferrer">
                      <ExternalLink size={14} />
                      二维码
                    </a>
                  )}
                </div>
                <div className="ops-action-cluster" aria-label={`${product.title} 库存状态`}>
                  <button
                    type="button"
                    onClick={() => void changeProductStatus(product, "listed")}
                    disabled={productLifecycleStatus === "loading" || product.lifecycle_status === "listed"}
                  >
                    上架
                  </button>
                  <button
                    type="button"
                    onClick={() => void changeProductStatus(product, "reserved")}
                    disabled={productLifecycleStatus === "loading" || product.lifecycle_status === "reserved"}
                  >
                    预留
                  </button>
                  <button
                    type="button"
                    onClick={() => void changeProductStatus(product, "sold")}
                    disabled={productLifecycleStatus === "loading" || product.lifecycle_status === "sold"}
                  >
                    成交
                  </button>
                  <button
                    type="button"
                    onClick={() => void changeProductStatus(product, "archived")}
                    disabled={productLifecycleStatus === "loading" || product.lifecycle_status === "archived"}
                  >
                    下架
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无真实商品库存</p>
        )}
      </section>

      <OpsList
        title="订单跟进"
        empty="暂无订单"
        items={orders}
        render={(order) => (
          <article className="ops-row" key={order.id}>
            <ListChecks size={18} />
            <div>
              <strong>{order.product_title ?? order.product_id}</strong>
              <span>{order.customer_name} · {order.city} · {order.contact}</span>
              <small>{translateOrderIntent(order.intent)} · {translateOrderStatus(order.status)}</small>
            </div>
            <button type="button" onClick={() => void advanceOrder(order)} disabled={order.status === "fulfilled" || order.status === "cancelled"}>
              推进
            </button>
          </article>
        )}
      />

      <section className="ops-section">
        <div className="mini-section-head">
          <Truck size={18} />
          <h3>履约交付</h3>
        </div>
        <form className="ops-product-form" onSubmit={submitFulfillmentTask}>
          <div className="ops-form-grid">
            <label>
              <span>订单号</span>
              <input name="orderId" inputMode="numeric" placeholder="订单 # 编号" required />
            </label>
            <label>
              <span>任务类型</span>
              <select name="taskType" defaultValue="delivery" required>
                <option value="delivery">同城配送</option>
                <option value="pickup">到店自提</option>
                <option value="handoff">交付验收</option>
                <option value="repair_return">售后返还</option>
              </select>
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>城市</span>
              <input name="city" placeholder="上海" required />
            </label>
            <label>
              <span>预约时间</span>
              <input name="scheduledAt" type="datetime-local" />
            </label>
          </div>
          <label>
            <span>交付地址</span>
            <input name="address" placeholder="门店、自提点或配送地址" required />
          </label>
          <div className="ops-form-grid">
            <label>
              <span>负责人</span>
              <input name="assignee" placeholder="运营 / 骑手 / 门店" />
            </label>
            <label>
              <span>状态</span>
              <select name="status" defaultValue="scheduled" required>
                <option value="new">待安排</option>
                <option value="scheduled">已预约</option>
                <option value="in_transit">交付中</option>
                <option value="completed">已完成</option>
                <option value="cancelled">已取消</option>
              </select>
            </label>
          </div>
          <label>
            <span>备注</span>
            <input name="operatorNote" placeholder="验收要求、配件、门店或路线说明" />
          </label>
          <button className="primary-button" type="submit" disabled={fulfillmentStatus === "loading"}>
            {fulfillmentStatus === "loading" ? "安排中" : "安排履约任务"}
          </button>
          {fulfillmentStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              履约任务已写入数据库
            </p>
          )}
          {fulfillmentStatus === "error" && <p className="form-message error">履约任务处理失败，请检查订单号和字段</p>}
        </form>
        {fulfillmentTasks.length > 0 ? (
          <div className="ops-list">
            {fulfillmentTasks.slice(0, 8).map((task) => (
              <article className="ops-row fulfillment-task" key={task.id}>
                <Truck size={18} />
                <div>
                  <strong>#{task.id} · {translateFulfillmentType(task.task_type)}</strong>
                  <span>
                    订单 #{task.order_id} · {task.product_title ?? task.product_id} · {translateFulfillmentStatus(task.status)}
                  </span>
                  <small>
                    {task.city} · {formatDateTime(task.scheduled_at)} · {task.assignee || "未分配"}
                  </small>
                  <small>{task.latest_note || task.operator_note || task.address}</small>
                </div>
                <button
                  type="button"
                  onClick={() => void advanceFulfillmentTask(task)}
                  disabled={fulfillmentStatus === "loading" || task.status === "completed" || task.status === "cancelled"}
                >
                  推进
                </button>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无履约任务</p>
        )}
      </section>

      <section className="ops-section">
        <div className="mini-section-head">
          <CircleDollarSign size={18} />
          <h3>收款 / 退款流水</h3>
        </div>
        <form className="ops-product-form" onSubmit={submitPaymentRecord}>
          <div className="ops-form-grid">
            <label>
              <span>订单号</span>
              <input name="orderId" inputMode="numeric" placeholder="订单 # 编号" required />
            </label>
            <label>
              <span>类型</span>
              <select name="paymentType" defaultValue="deposit" required>
                <option value="deposit">订金</option>
                <option value="final">尾款</option>
                <option value="refund">退款</option>
                <option value="adjustment">调整</option>
              </select>
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>金额</span>
              <input name="amount" type="number" inputMode="decimal" step="0.01" placeholder="单位：元" required />
            </label>
            <label>
              <span>渠道</span>
              <select name="channel" defaultValue="wechat" required>
                <option value="wechat">微信</option>
                <option value="alipay">支付宝</option>
                <option value="bank">银行</option>
                <option value="cash">现金</option>
                <option value="other">其他</option>
              </select>
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>状态</span>
              <select name="status" defaultValue="confirmed" required>
                <option value="confirmed">已确认</option>
                <option value="pending">待确认</option>
                <option value="void">作废</option>
              </select>
            </label>
            <label>
              <span>外部单号</span>
              <input name="externalRef" placeholder="支付流水号可留空" />
            </label>
          </div>
          <label>
            <span>备注</span>
            <input name="operatorNote" placeholder="收款人、退款原因或对账说明" />
          </label>
          <button className="primary-button" type="submit" disabled={paymentStatus === "loading"}>
            {paymentStatus === "loading" ? "记录中" : "记录资金流水"}
          </button>
          {paymentStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              资金流水已写入数据库
            </p>
          )}
          {paymentStatus === "error" && <p className="form-message error">资金流水记录失败，请检查订单号和字段</p>}
        </form>
        {paymentRecords.length > 0 ? (
          <div className="ops-list">
            {paymentRecords.slice(0, 6).map((payment) => (
              <article className="ops-row payment-record" key={payment.id}>
                <CircleDollarSign size={18} />
                <div>
                  <strong>#{payment.order_id} · {translatePaymentType(payment.payment_type)} · {currency.format(payment.amount)}</strong>
                  <span>{payment.product_title ?? payment.product_id} · {translatePaymentChannel(payment.channel)} · {translatePaymentStatus(payment.status)}</span>
                  <small>{payment.external_ref || payment.operator_note || "暂无备注"}</small>
                </div>
                <span className="ops-rank">#{payment.id}</span>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无资金流水</p>
        )}
      </section>

      <OpsList
        title="售后质保"
        empty="暂无售后工单"
        items={serviceCases}
        render={(serviceCase) => (
          <article className="ops-row service-case" key={serviceCase.id}>
            <HeartHandshake size={18} />
            <div>
              <strong>#{serviceCase.id} · {translateServiceIssue(serviceCase.issue_type)}</strong>
              <span>
                {serviceCase.product_title ?? serviceCase.product_id ?? "未关联商品"} · {serviceCase.customer_name} · {serviceCase.contact}
              </span>
              <small>{translateServiceStatus(serviceCase.status)} · {serviceCase.latest_note ?? serviceCase.description}</small>
            </div>
            <button
              type="button"
              onClick={() => void advanceServiceCase(serviceCase)}
              disabled={serviceCaseStatus === "loading" || serviceCase.status === "closed"}
            >
              推进
            </button>
          </article>
        )}
      />
      {serviceCaseStatus === "done" && (
        <p className="form-message success">
          <CheckCircle2 size={16} />
          售后状态已更新
        </p>
      )}
      {serviceCaseStatus === "error" && <p className="form-message error">售后状态更新失败</p>}

      <section className="ops-section">
        <div className="mini-section-head">
          <CircleDollarSign size={18} />
          <h3>正式回收报价单</h3>
        </div>
        <form className="ops-product-form" onSubmit={submitIntakeQuote}>
          <div className="ops-form-grid">
            <label>
              <span>线索 ID</span>
              <input name="intakeLeadId" inputMode="numeric" placeholder="回收线索 # 编号" required />
            </label>
            <label>
              <span>报价金额</span>
              <input name="quoteAmount" type="number" inputMode="decimal" step="0.01" placeholder="真实可承诺回收价" required />
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>预计上架价</span>
              <input name="expectedListPrice" type="number" inputMode="decimal" step="0.01" placeholder="可留空" />
            </label>
            <label>
              <span>有效期</span>
              <input name="validUntil" type="datetime-local" />
            </label>
          </div>
          <label>
            <span>报价状态</span>
            <select name="status" defaultValue="sent" required>
              <option value="sent">已发送</option>
              <option value="draft">草稿</option>
              <option value="accepted">已接受</option>
              <option value="declined">已拒绝</option>
              <option value="expired">已过期</option>
            </select>
          </label>
          <label>
            <span>报价依据</span>
            <input name="operatorNote" placeholder="成色、价格证据、整备预估和沟通结论" />
          </label>
          <button className="primary-button" type="submit" disabled={intakeQuoteStatus === "loading"}>
            {intakeQuoteStatus === "loading" ? "保存中" : "保存报价单"}
          </button>
          {intakeQuoteStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              回收报价单已写入数据库
            </p>
          )}
          {intakeQuoteStatus === "error" && <p className="form-message error">报价单处理失败，请检查线索 ID、金额和状态</p>}
        </form>
        {intakeQuotes.length > 0 ? (
          <div className="ops-list">
            {intakeQuotes.slice(0, 8).map((quote) => (
              <article className="ops-row intake-quote" key={quote.id}>
                <CircleDollarSign size={18} />
                <div>
                  <strong>#{quote.id} · {quote.lead_title}</strong>
                  <span>
                    {quote.city} · 报价 {currency.format(quote.quote_amount)}
                    {quote.expected_list_price ? ` · 预计上架 ${currency.format(quote.expected_list_price)}` : ""}
                  </span>
                  <small>
                    线索 #{quote.intake_lead_id} · {translateIntakeQuoteStatus(quote.status)}
                    {quote.valid_until ? ` · 有效至 ${formatDateTime(quote.valid_until)}` : ""}
                  </small>
                  <small>{quote.latest_note || quote.operator_note || quote.lead_contact}</small>
                  {quote.procurement_order_id && (
                    <small>
                      已转采购 #{quote.procurement_order_id} · {translateProcurementStatus(quote.procurement_status ?? "new")}
                    </small>
                  )}
                </div>
                <div className="ops-action-cluster" aria-label={`${quote.lead_title} 报价状态`}>
                  <button
                    type="button"
                    onClick={() => void changeIntakeQuoteStatus(quote, "sent")}
                    disabled={intakeQuoteStatus === "loading" || quote.status === "sent" || quote.status === "converted"}
                  >
                    发送
                  </button>
                  <button
                    type="button"
                    onClick={() => void changeIntakeQuoteStatus(quote, "accepted")}
                    disabled={intakeQuoteStatus === "loading" || quote.status === "accepted" || quote.status === "converted"}
                  >
                    接受
                  </button>
                  <button
                    type="button"
                    onClick={() => void changeIntakeQuoteStatus(quote, "declined")}
                    disabled={intakeQuoteStatus === "loading" || quote.status === "declined" || quote.status === "converted"}
                  >
                    拒绝
                  </button>
                  <button
                    type="button"
                    onClick={() => void convertIntakeQuote(quote)}
                    disabled={
                      intakeQuoteStatus === "loading" ||
                      quote.status === "converted" ||
                      quote.status === "declined" ||
                      quote.status === "expired"
                    }
                  >
                    采购
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无正式回收报价单</p>
        )}
      </section>

      <section className="ops-section">
        <div className="mini-section-head">
          <UploadCloud size={18} />
          <h3>回收报价 / 采收</h3>
        </div>
        <form className="ops-product-form" onSubmit={submitIntakeEvent}>
          <div className="ops-form-grid">
            <label>
              <span>线索 ID</span>
              <input name="leadId" inputMode="numeric" placeholder="列表中的 # 编号" required />
            </label>
            <label>
              <span>动作</span>
              <select name="eventType" defaultValue="quote" required>
                <option value="quote">报价</option>
                <option value="schedule">预约</option>
                <option value="acquire">采收</option>
                <option value="reject">放弃</option>
                <option value="note">备注</option>
              </select>
            </label>
          </div>
          <div className="ops-form-grid">
            <label>
              <span>金额</span>
              <input name="amount" type="number" inputMode="decimal" step="0.01" placeholder="回收价或采收成本" />
            </label>
            <label>
              <span>预约时间</span>
              <input name="scheduledAt" type="datetime-local" />
            </label>
          </div>
          <label>
            <span>关联商品 ID</span>
            <input name="productId" placeholder="采收后上架商品可填写" />
          </label>
          <label>
            <span>备注</span>
            <input name="operatorNote" placeholder="成色、风险、沟通结论" />
          </label>
          <button className="primary-button" type="submit" disabled={intakeEventStatus === "loading"}>
            {intakeEventStatus === "loading" ? "记录中" : "记录回收动作"}
          </button>
          {intakeEventStatus === "done" && (
            <p className="form-message success">
              <CheckCircle2 size={16} />
              回收动作已写入数据库
            </p>
          )}
          {intakeEventStatus === "error" && <p className="form-message error">回收动作记录失败，请检查线索 ID 和字段</p>}
        </form>
        {intakeLeads.length > 0 ? (
          <div className="ops-list">
            {intakeLeads.map((lead) => (
              <article className="ops-row intake" key={lead.id}>
                <UploadCloud size={18} />
                <div>
                  <strong>#{lead.id} · {lead.title}</strong>
                  <span>{lead.city} · 期望 {currency.format(lead.expected_price)} · {lead.contact}</span>
                  <small>
                    {lead.category === "mobility" ? "低碳出行" : "母婴耐用品"} · {translateIntakeStatus(lead.status)}
                    {lead.latest_event_type ? ` · ${translateIntakeEventType(lead.latest_event_type)}` : ""}
                    {lead.latest_amount ? ` · ${currency.format(lead.latest_amount)}` : ""}
                  </small>
                  {lead.latest_note && <small>{lead.latest_note}</small>}
                </div>
                <button type="button" onClick={() => void advanceIntake(lead)} disabled={lead.status === "closed"}>
                  推进
                </button>
              </article>
            ))}
          </div>
        ) : (
          <p className="ops-empty">暂无回收线索</p>
        )}
      </section>
    </div>
  );
}

function MetricTile({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <article>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function OpsList<T>({
  title,
  empty,
  items,
  render
}: {
  title: string;
  empty: string;
  items: T[];
  render: (item: T) => React.ReactNode;
}) {
  return (
    <section className="ops-section">
      <div className="mini-section-head">
        <ListChecks size={18} />
        <h3>{title}</h3>
      </div>
      {items.length > 0 ? <div className="ops-list">{items.map(render)}</div> : <p className="ops-empty">{empty}</p>}
    </section>
  );
}

function sumStatus(rows: Array<{ count: number }>) {
  return rows.reduce((sum, row) => sum + Number(row.count), 0);
}

function formatStatus(rows: Array<{ status: string; count: number }>) {
  if (rows.length === 0) {
    return "暂无";
  }

  return rows.map((row) => `${translateStatus(row.status)} ${row.count}`).join(" / ");
}

function formatServiceStatus(rows: Array<{ status: AdminServiceCase["status"]; count: number }>) {
  if (rows.length === 0) {
    return "暂无";
  }

  return rows.map((row) => `${translateServiceStatus(row.status)} ${row.count}`).join(" / ");
}

function formatFulfillmentStatus(rows: Array<{ status: AdminFulfillmentTask["status"]; count: number }>) {
  if (rows.length === 0) {
    return "暂无";
  }

  return rows.map((row) => `${translateFulfillmentStatus(row.status)} ${row.count}`).join(" / ");
}

function formatPartnerOfferStatus(rows: Array<{ status: PartnerOfferStatus; count: number }>) {
  if (rows.length === 0) {
    return "暂无";
  }

  return rows.map((row) => `${translatePartnerOfferStatus(row.status)} ${row.count}`).join(" / ");
}

function formatMarginRate(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === "") {
    return "毛利率待确认";
  }

  const rate = Number(value);
  if (!Number.isFinite(rate)) {
    return "毛利率待确认";
  }

  return `毛利率 ${Math.round(rate * 100)}%`;
}

function formatWatchPriority(priority: "watch" | "hot" | "must_buy") {
  return {
    watch: "关注",
    hot: "热推",
    must_buy: "必抢"
  }[priority];
}

function formatNotificationChannel(channel: AdminNotificationMessage["channel"]) {
  return {
    site: "站内信",
    wechat_template: "微信模板"
  }[channel];
}

function formatNotificationStatus(status: AdminNotificationMessage["status"]) {
  return {
    unread: "未读",
    read: "已读",
    sent: "已发送",
    failed: "失败"
  }[status];
}

function toDateTimeLocalValue(value = new Date()) {
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function formatLedgerDateTime(value: string | null) {
  if (!value) {
    return "未记";
  }

  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function formatDateTime(value: string | null) {
  if (!value) {
    return "未预约";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "时间待确认";
  }

  return date.toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function translateStatus(status: string) {
  return {
    new: "新",
    confirmed: "确认",
    paid: "付款",
    fulfilled: "完成",
    cancelled: "取消",
    quoted: "报价",
    scheduled: "预约",
    closed: "关闭"
  }[status] ?? status;
}

function translateOrderIntent(intent: AdminOrder["intent"]) {
  return { reserve: "预约看货", buy: "锁定购买", rent: "先租后买" }[intent];
}

function translateOrderStatus(status: AdminOrder["status"]) {
  return translateStatus(status);
}

function translateServiceStatus(status: AdminServiceCase["status"]) {
  return {
    new: "新工单",
    in_review: "审核",
    resolving: "处理中",
    resolved: "已解决",
    closed: "已关闭"
  }[status];
}

function translateServiceIssue(issueType: AdminServiceCase["issue_type"]) {
  return {
    warranty: "质保",
    repair: "维修",
    return: "退换",
    safety: "安全",
    other: "其他"
  }[issueType];
}

function translateFulfillmentStatus(status: AdminFulfillmentTask["status"]) {
  return {
    new: "待安排",
    scheduled: "已预约",
    in_transit: "交付中",
    completed: "已完成",
    cancelled: "已取消"
  }[status];
}

function translateFulfillmentType(type: AdminFulfillmentTask["task_type"]) {
  return {
    pickup: "到店自提",
    delivery: "同城配送",
    handoff: "交付验收",
    repair_return: "售后返还"
  }[type];
}

function translatePaymentType(type: PaymentType) {
  return {
    deposit: "订金",
    final: "尾款",
    refund: "退款",
    adjustment: "调整"
  }[type];
}

function translateProcurementPaymentType(type: ProcurementPaymentType) {
  return {
    deposit: "订金",
    balance: "尾款",
    full: "全款",
    refund: "退款",
    adjustment: "调整"
  }[type];
}

function translatePaymentChannel(channel: PaymentChannel) {
  return {
    wechat: "微信",
    alipay: "支付宝",
    bank: "银行",
    cash: "现金",
    other: "其他"
  }[channel];
}

function translatePaymentStatus(status: PaymentStatus) {
  return {
    pending: "待确认",
    confirmed: "已确认",
    void: "作废"
  }[status];
}

function translateIntakeStatus(status: AdminIntakeLead["status"]) {
  return translateStatus(status);
}

function translateIntakeEventType(eventType: IntakeEventType) {
  return {
    quote: "已报价",
    schedule: "已预约",
    acquire: "已采收",
    reject: "已放弃",
    note: "备注"
  }[eventType];
}

function translateIntakeQuoteStatus(status: IntakeQuoteStatus) {
  return {
    draft: "草稿",
    sent: "已发送",
    accepted: "已接受",
    declined: "已拒绝",
    expired: "已过期",
    converted: "已转采购"
  }[status];
}

function translateReadinessStatus(status: AdminReadiness["status"]) {
  return {
    ready: "可运营",
    needs_data: "待补数据",
    misconfigured: "需配置"
  }[status];
}

function translateInsightType(type: MarketInsight["insightType"]) {
  return {
    supply: "供给",
    pricing: "定价",
    inspection: "质检",
    fulfillment: "履约",
    capital: "资本",
    category: "品类",
    growth: "增长"
  }[type];
}

function translatePriceEvidenceSource(source: PriceEvidenceSource) {
  return {
    xianyu: "闲鱼授权",
    partner: "合作供给",
    manual: "人工",
    competitor: "竞品"
  }[source];
}

function translateCategory(category: Category) {
  return {
    mobility: "绿色出行",
    baby: "母婴耐用"
  }[category];
}

function translateOpportunitySource(source: Opportunity["source"]) {
  return {
    xianyu: "闲鱼",
    partner: "合作",
    manual: "人工"
  }[source];
}

function translateOpportunityRisk(risk: Opportunity["risk"]) {
  return {
    low: "低风险",
    medium: "需复核",
    high: "高风险"
  }[risk];
}

function resolveOpportunitySourceUrl(opportunity: Pick<Opportunity, "id" | "source" | "sourceUrl" | "sellerSignal">) {
  return resolvePlatformItemUrl({
    id: opportunity.id,
    source: opportunity.source,
    sourceUrl: opportunity.sourceUrl ?? null,
    sellerSignal: opportunity.sellerSignal
  });
}

function resolveAdminOpportunitySourceUrl(opportunity: AdminOpportunity) {
  return resolvePlatformItemUrl({
    id: opportunity.id,
    source: opportunity.source,
    sourceUrl: opportunity.source_url,
    sellerSignal: opportunity.seller_signal
  });
}

function resolvePlatformItemUrl(input: {
  id: string;
  source: Opportunity["source"];
  sourceUrl: string | null;
  sellerSignal: string;
}) {
  if (input.sourceUrl && /^https?:\/\//i.test(input.sourceUrl)) {
    return input.sourceUrl;
  }

  if (input.source === "xianyu") {
    const visibleXianyuMatch = /^xianyu-visible-([a-z0-9_-]+)$/i.exec(input.id);
    if (visibleXianyuMatch) {
      return `https://www.goofish.com/item?id=${visibleXianyuMatch[1]}`;
    }
  }

  return input.sellerSignal.match(/https:\/\/(?:www\.)?goofish\.com\/item\?[^\s|，,'"]+/i)?.[0] ?? null;
}

function formatSellerSignal(signal: string) {
  return signal
    .replace(/https:\/\/(?:www\.)?goofish\.com\/item\?[^\s|，,'"]+/gi, "")
    .replace(/\s*·\s*$/g, "")
    .replace(/\s{2,}/g, " ")
    .trim() || "待核验卖家";
}

function readXianyuVisibleDefaults(formData: FormData) {
  return {
    category: (formData.get("category") === "baby" ? "baby" : "mobility") as Category,
    subcategory: String(formData.get("subcategory") ?? ""),
    city: String(formData.get("city") ?? ""),
    condition: String(formData.get("condition") ?? "")
  };
}

function translateXianyuVisibleRejectReason(reason: string) {
  if (reason.includes("Goofish item URL") || reason.includes("itemUrl")) {
    return "缺少有效闲鱼商品链接";
  }
  if (reason.includes("ask price") || reason.includes("positive")) {
    if (reason.includes("fair")) {
      return "缺少有效参考卖价";
    }
    return "缺少有效报价";
  }
  if (reason.includes("too_small")) {
    return "字段内容过短";
  }
  return reason;
}

function translateCarbonLedgerType(type: CarbonLedgerEntryType) {
  return {
    reuse_sale: "复用成交",
    procurement: "回收采买",
    refurbishment: "整备延寿",
    repair: "维修延寿",
    recycle: "回收处置",
    manual_adjustment: "人工调整"
  }[type];
}

function translateSupplierPartnerType(type: SupplierPartnerType) {
  return {
    bike_shop: "出行门店",
    baby_store: "母婴门店",
    community: "社区",
    repair_shop: "维修点",
    institution: "机构",
    other: "其他"
  }[type];
}

function translateSupplierPartnerStatus(status: SupplierPartnerStatus) {
  return {
    lead: "待跟进",
    active: "活跃",
    paused: "暂停",
    rejected: "淘汰"
  }[status];
}

function translatePartnerOfferStatus(status: PartnerOfferStatus) {
  return {
    new: "新供给",
    evaluating: "验货中",
    accepted: "已接受",
    rejected: "已拒绝",
    converted: "已转库存"
  }[status];
}

function translateProcurementSource(source: ProcurementSourceType) {
  return {
    opportunity: "机会线索",
    partner_offer: "合作报价",
    intake_lead: "回收线索",
    manual: "人工来源"
  }[source];
}

function translateProcurementStatus(status: ProcurementStatus) {
  return {
    new: "新采购",
    negotiating: "议价中",
    paid: "已付款",
    received: "已到货",
    listed: "已上架",
    cancelled: "已取消"
  }[status];
}

function translateProcurementExecutionPriority(priority: AdminProcurementExecutionItem["priority"]) {
  return {
    high: "高",
    medium: "中",
    low: "低"
  }[priority];
}

function translateProcurementSellerRiskLevel(level: ProcurementSellerRiskLevel) {
  return {
    pass: "风控通过",
    review: "需复核",
    blocked: "已拦截"
  }[level];
}

function translateProcurementPaymentStatus(status: ProcurementPaymentStatus) {
  return {
    unpaid: "未付款",
    partial: "部分付款",
    paid: "已付款",
    refunded: "已退款"
  }[status];
}

function translateRefurbishmentType(type: RefurbishmentTaskType) {
  return {
    cleaning: "清洁消杀",
    repair: "维修整备",
    battery_check: "电池检测",
    safety_check: "安全复检",
    photo: "拍照建档",
    listing_prep: "上架准备"
  }[type];
}

function translateRefurbishmentStatus(status: RefurbishmentStatus) {
  return {
    new: "待整备",
    in_progress: "整备中",
    blocked: "有阻塞",
    qc_passed: "质检通过",
    qc_failed: "质检未过",
    completed: "已完成",
    cancelled: "已取消"
  }[status];
}

function translateProductInspectionStatus(status: ProductInspectionStatus) {
  return {
    draft: "草稿",
    passed: "通过",
    failed: "未通过",
    needs_repair: "需返修",
    archived: "归档"
  }[status];
}

function formatPublicDate(value: string | null) {
  if (!value) {
    return "未记录";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "未记录";
  }

  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

function translatePricingConfidence(confidence: AdminPricingGuide["confidence"]) {
  return {
    high: "高置信",
    medium: "中置信",
    low: "低置信",
    none: "无证据"
  }[confidence];
}

function translateProductStatus(status: ProductStatus) {
  return {
    draft: "草稿",
    listed: "上架可售",
    reserved: "已预留",
    sold: "已成交",
    archived: "已下架"
  }[status];
}

function translateInspectionTemplateStatus(status: InspectionTemplateStatus) {
  return {
    draft: "草稿",
    active: "启用",
    archived: "归档"
  }[status];
}

function translateOpportunityAction(status: string | null) {
  return {
    watch: "盯盘",
    bid: "建议采买",
    inspect: "安排验货",
    contacted: "已联系",
    inspection_scheduled: "已约验货",
    purchased: "已采买",
    rejected: "已放弃"
  }[status ?? ""] ?? "待处理";
}

function translateImportStatus(status: AdminOpportunityImportRun["status"]) {
  return {
    running: "同步中",
    success: "同步成功",
    failed: "同步失败"
  }[status];
}

function translateImportShortStatus(status: AdminOpportunityImportRun["status"]) {
  return {
    running: "中",
    success: "成",
    failed: "错"
  }[status];
}

function translateImportTrigger(trigger: AdminOpportunityImportRun["triggered_by"]) {
  return {
    admin: "人工刷新",
    schedule: "定时刷新",
    system: "系统"
  }[trigger];
}

function EmptyState({ label }: { label: string }) {
  return (
    <section className="empty-state">
      <Search size={28} />
      <p>{label}</p>
    </section>
  );
}
